import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getEntityId } from 'helpers/entityId';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Modal,
  Alert,
  Collapse
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faUndo,
  faDownload,
  faSave,
  faInfoCircle,
  faCheckCircle,
  faQuestionCircle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import {
  Document,
  Page,
  Text,
  View,
  PDFDownloadLink,
  Image,
  pdf
} from '@react-pdf/renderer';
import hocLogo from '../../../assets/img/icons/favicon-100x100.png';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Job, CREATE_JOB_ACTIVITY_LOG, SEARCH_JOBS } from 'services/api/job';
import { uploadFileWithJob } from 'services/api/file';
import { useAccessToken } from 'providers/AccessTokenProvider';
import Select from 'react-select';
import { debounce } from 'lodash';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  MarkPointComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from 'providers/AppProvider';
import { rgbaColor } from 'helpers/utils';
import './pressure-drop.css';
import {
  BIT_METRICS,
  CalculatorInputsSnapshot,
  Constants,
  DEFAULT_CONSTANTS,
  FIXED_TWO_DECIMAL_METRICS,
  JetErrorsMap,
  JetRow,
  JetRowErrors,
  JetRowItemProps,
  JetRowSnapshot,
  MetricDescriptor,
  ONBOARDING_STORAGE_KEY,
  PressureDropJobOption,
  PressureDropSeriesPoint,
  ResultsState,
  ScenarioResult,
  TimelineDiffItem,
  TimelineEntry,
  TimelineSummaryItem,
  TOOL_LIMITS,
  TOOL_SERIES_MAP,
  TOOL_THEME_PRESETS,
  TOOL_TYPE_OPTIONS,
  UR_METRICS,
  SHEAR_SCREW_DEFAULTS,
  SHEAR_SCREW_TOOL_KEYS
} from './types/types';
import { pdfStyles } from './styles/styles';

echarts.use([
  GridComponent,
  TooltipComponent,
  TitleComponent,
  MarkPointComponent,
  LineChart,
  CanvasRenderer
]);

const mixHexColor = (hex: string, blend: string, amount: number) => {
  const parse = (h: string) => {
    const normalized = h.replace('#', '');
    const bigint = parseInt(normalized, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  };
  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  const c1 = parse(hex);
  const c2 = parse(blend);
  const r = Math.round(c1.r * (1 - amount) + c2.r * amount);
  const g = Math.round(c1.g * (1 - amount) + c2.g * amount);
  const b = Math.round(c1.b * (1 - amount) + c2.b * amount);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const withAlpha = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// PDF Document Component
const ResultsPDF = ({
  results,
  mudWt,
  flowRate,
  comparisonRate,
  bitDiameter,
  urDiameter,
  generatedAt,
  generatedBy,
  bitSummary,
  urSummary,
  parameterAlerts,
  toolType,
  toolSeries,
  isGenericMode,
  shearData
}: {
  results: ResultsState;
  mudWt: string;
  flowRate: string;
  comparisonRate: string;
  bitDiameter: string;
  urDiameter: string;
  generatedAt: string;
  generatedBy?: string;
  bitSummary?: string;
  urSummary?: string;
  parameterAlerts: string[];
  toolType: string;
  toolSeries: string;
  isGenericMode: boolean;
  shearData?: {
    toolKey: string;
    screwCount: string;
    tensileStrength: string;
    minorDiaArea: string;
    shearValuePct: string;
    psiToMoveArms: string;
    pistonArea: string;
    springUsed: boolean;
    springPSI: string;
    shearPSI: number;
    pressureDropToOpen: number;
    stayBelow: number;
  } | null;
}) => {
  const formatPdfMetricValue = (metricKey: string, value?: number | null) => {
    if (metricKey === 'Jet Impact Force') {
      return formatJetImpactForce(value);
    }
    if (FIXED_TWO_DECIMAL_METRICS.has(metricKey)) {
      return formatFixed(value);
    }
    return formatSignificant(value, 2);
  };

  const scenarioCount = results.scenarios.length;

  const chunkInputs = <T,>(items: T[], size: number): T[][] => {
    const result: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
      result.push(items.slice(i, i + size));
    }
    return result;
  };

  const toolTypeDisplay = isGenericMode ? 'Generic' : toolType?.trim() || '-';
  const toolSeriesDisplay = isGenericMode ? 'N/A' : toolSeries?.trim() || '-';

  const inputPairs = chunkInputs(
    [
      { label: 'Tool Type', value: toolTypeDisplay },
      { label: 'Tool Series', value: toolSeriesDisplay },
      { label: 'Mud Weight (ppg)', value: mudWt || '-' },
      { label: 'Flow Rate (gpm)', value: flowRate || '-' },
      { label: 'UR Dia (in)', value: urDiameter || '-' },
      { label: 'Bit Dia (in)', value: bitDiameter || '-' },
      { label: 'Comparison Rate (%)', value: comparisonRate || '-' },
      { label: 'Generated At', value: generatedAt || '-' }
    ],
    2
  );

  const renderScenarioTable = (
    title: string,
    metrics: readonly MetricDescriptor[],
    key: 'bit' | 'ur',
    position: 'left' | 'right'
  ) => {
    if (!scenarioCount) return null;

    return (
      <View
        style={
          position === 'left'
            ? [pdfStyles.tableSection, pdfStyles.tableSectionLeft]
            : [pdfStyles.tableSection, pdfStyles.tableSectionRight]
        }
        wrap={false}
      >
        <Text style={pdfStyles.tableTitle}>{title}</Text>
        <View style={pdfStyles.tableContainer}>
          <View style={pdfStyles.tableHeaderRow}>
            <View style={pdfStyles.metricCellWrap}>
              <Text style={pdfStyles.headerPrimary}>Metric</Text>
            </View>
            {results.scenarios.map((scenario, columnIdx) => (
              <View
                key={`${scenario.label}-${scenario.deltaLabel}`}
                style={
                  columnIdx === results.scenarios.length - 1
                    ? [pdfStyles.valueCellWrap, pdfStyles.valueCellLast]
                    : pdfStyles.valueCellWrap
                }
              >
                <Text style={pdfStyles.headerPrimary}>{scenario.label}</Text>
                <Text style={pdfStyles.headerSecondary}>
                  {scenario.deltaLabel}
                </Text>
              </View>
            ))}
          </View>
          {metrics.map((metric, rowIdx) => (
            <View
              style={
                rowIdx % 2 === 1
                  ? [pdfStyles.tableRow, pdfStyles.tableRowAlt]
                  : pdfStyles.tableRow
              }
              key={metric.key}
            >
              <View style={pdfStyles.metricCellWrap}>
                <Text style={pdfStyles.metricLabel}>{metric.label}</Text>
              </View>
              {results.scenarios.map((scenario, columnIdx) => (
                <View
                  key={`${scenario.label}-${scenario.deltaLabel}-${metric.key}`}
                  style={
                    columnIdx === results.scenarios.length - 1
                      ? [pdfStyles.valueCellWrap, pdfStyles.valueCellLast]
                      : pdfStyles.valueCellWrap
                  }
                >
                  <Text style={pdfStyles.cellValue}>
                    {formatPdfMetricValue(
                      metric.key,
                      scenario[key][metric.key]
                    )}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={pdfStyles.page}
        wrap={false}
      >
        <View style={pdfStyles.header}>
          <View style={pdfStyles.headerRow}>
            <View style={pdfStyles.logoWrap}>
              <Image style={pdfStyles.logoImage} src={hocLogo} />
            </View>
            <View style={pdfStyles.headerTitleBlock}>
              <Text style={pdfStyles.title}>
                HOC Pressure Drop Calculator Results
              </Text>
              <Text style={pdfStyles.subtitle}>
                Hole Opener Corp · Pressure Drop Summary
              </Text>
            </View>
            <View style={pdfStyles.logoWrap} />
          </View>
        </View>

        <View style={pdfStyles.inputTable} wrap={false}>
          {inputPairs.map((row, rowIdx) => (
            <View
              key={rowIdx}
              style={
                rowIdx === inputPairs.length - 1
                  ? [pdfStyles.inputRow, pdfStyles.inputRowLast]
                  : pdfStyles.inputRow
              }
            >
              {row.map((cell, cellIdx) => (
                <View
                  key={cell.label}
                  style={
                    cellIdx === row.length - 1
                      ? [pdfStyles.inputCell, pdfStyles.inputCellLastInRow]
                      : pdfStyles.inputCell
                  }
                >
                  <Text style={pdfStyles.inputLabel}>{cell.label}</Text>
                  <Text style={pdfStyles.inputValue}>{cell.value}</Text>
                </View>
              ))}
              {row.length < 2 && (
                <View
                  key={`placeholder-${rowIdx}`}
                  style={[pdfStyles.inputCell, pdfStyles.inputCellLastInRow]}
                />
              )}
            </View>
          ))}
        </View>

        {results.totalTFA !== null && (
          <View style={pdfStyles.summaryRow} wrap={false}>
            <View style={pdfStyles.summaryCard}>
              <Text style={pdfStyles.summaryLabel}>Total TFA (In2)</Text>
              <Text style={pdfStyles.summaryValue}>
                {formatSignificant(results.totalTFA, 3)}
              </Text>
            </View>
          </View>
        )}

        {parameterAlerts.length > 0 && (
          <View style={pdfStyles.alertBox} wrap={false}>
            <Text style={pdfStyles.alertTitle}>Parameter Warnings</Text>
            {parameterAlerts.map((warning, index) => (
              <Text key={`warning-${index}`} style={pdfStyles.alertItem}>
                - {warning}
              </Text>
            ))}
          </View>
        )}

        {scenarioCount > 0 && (
          <View style={pdfStyles.tablesRow} wrap={false}>
            {renderScenarioTable(
              bitSummary ? `Bit Data ${bitSummary}` : 'Bit Data',
              BIT_METRICS,
              'bit',
              'left'
            )}
            {renderScenarioTable(
              urSummary ? `Underreamer Data ${urSummary}` : 'Underreamer Data',
              UR_METRICS,
              'ur',
              'right'
            )}
          </View>
        )}

        {shearData && (
          <View style={pdfStyles.shearSection} wrap={false}>
            <Text style={pdfStyles.shearSectionTitle}>
              Brass Shear Screw Calculator — {shearData.toolKey}
            </Text>
            <View style={pdfStyles.shearInputRow}>
              <View style={pdfStyles.shearInputCell}>
                <Text style={pdfStyles.inputLabel}>No. of Screws</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.screwCount || '0'}
                </Text>
              </View>
              <View style={pdfStyles.shearInputCell}>
                <Text style={pdfStyles.inputLabel}>Tensile Strength (psi)</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.tensileStrength || '-'}
                </Text>
              </View>
              <View style={pdfStyles.shearInputCell}>
                <Text style={pdfStyles.inputLabel}>Minor Dia Area (in²)</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.minorDiaArea || '-'}
                </Text>
              </View>
              <View style={pdfStyles.shearInputCell}>
                <Text style={pdfStyles.inputLabel}>Shear Value (%)</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.shearValuePct || '-'}%
                </Text>
              </View>
              <View style={pdfStyles.shearInputCell}>
                <Text style={pdfStyles.inputLabel}>PSI to Move Arms</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.psiToMoveArms || '0'}
                </Text>
              </View>
              <View style={pdfStyles.shearInputCell}>
                <Text style={pdfStyles.inputLabel}>Piston Area (in²)</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.pistonArea || '-'}
                </Text>
              </View>
              <View style={[pdfStyles.shearInputCell, { marginRight: 0 }]}>
                <Text style={pdfStyles.inputLabel}>Spring Used</Text>
                <Text style={pdfStyles.inputValue}>
                  {shearData.springUsed
                    ? `Yes (${shearData.springPSI} psi)`
                    : 'No'}
                </Text>
              </View>
            </View>
            <View style={pdfStyles.shearOutputRow}>
              <View style={pdfStyles.shearOutputCell}>
                <Text style={pdfStyles.shearOutputLabel}>Shear PSI</Text>
                <Text style={pdfStyles.shearOutputValue}>
                  {formatFixed(shearData.shearPSI, 2)}
                </Text>
              </View>
              <View style={pdfStyles.shearOutputCell}>
                <Text style={pdfStyles.shearOutputLabel}>
                  Pressure Drop to Open (psi)
                </Text>
                <Text style={pdfStyles.shearOutputValue}>
                  {formatFixed(shearData.pressureDropToOpen, 2)}
                </Text>
              </View>
              <View
                style={[
                  pdfStyles.shearOutputCell,
                  pdfStyles.shearOutputCellWarning
                ]}
              >
                <Text style={pdfStyles.shearOutputLabel}>
                  Stay Below to Drill Out (psi)
                </Text>
                <Text
                  style={[
                    pdfStyles.shearOutputValue,
                    pdfStyles.shearWarningValue
                  ]}
                >
                  {formatFixed(shearData.stayBelow, 2)}
                </Text>
                <Text style={pdfStyles.shearWarningNote}>
                  Stay below this pressure when drilling out shear screws
                </Text>
              </View>
            </View>
          </View>
        )}

        <View style={pdfStyles.footer} wrap={false}>
          <Text style={pdfStyles.footerLink}>www.holeopener.com</Text>
          <Text>
            {`Last calculated by ${generatedBy || 'Unknown user'}${
              generatedAt ? ` on ${generatedAt}` : ''
            }`}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Helpers
const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const parseNumber = (s: string, fallback = 0) => {
  const n = Number((s || '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : fallback;
};
const parsePercent = (s: string, fallback = 0) => {
  const cleaned = (s || '').replace(/%/g, '').replace(/,/g, '').trim();
  if (!cleaned) return fallback;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value / 100 : fallback;
};
const round = (n: number, d = 3) =>
  Math.round((n + Number.EPSILON) * Math.pow(10, d)) / Math.pow(10, d);
const normalizeJetSizeInput = (input: string) =>
  input.replace(/"+/g, '').trim();

const getJetFractionParts = (
  input: string
): { numerator: number; denominator: number } | null => {
  const normalized = normalizeJetSizeInput(input);
  if (!normalized) return null;
  const slashIndex = normalized.indexOf('/');
  if (slashIndex === -1) {
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) return null;
    const rounded = Math.round(numeric);
    if (Math.abs(rounded - numeric) > 1e-6) return null;
    return { numerator: rounded, denominator: 32 };
  }
  const numeratorStr = normalized.slice(0, slashIndex).trim();
  const denominatorStr = normalized.slice(slashIndex + 1).trim();
  const numerator = Number(numeratorStr);
  const denominator = Number(denominatorStr || '0');
  if (
    !Number.isFinite(numerator) ||
    !Number.isFinite(denominator) ||
    !Number.isInteger(numerator) ||
    !Number.isInteger(denominator) ||
    denominator === 0
  ) {
    return null;
  }
  return { numerator, denominator };
};

const parseJetDiameterInches = (input: string): number | null => {
  const parts = getJetFractionParts(input);
  if (!parts || parts.denominator !== 32) return null;
  return parts.numerator / parts.denominator;
};

const formatJetSizeForInput = (input: string): string => {
  const normalized = normalizeJetSizeInput(input);
  if (!normalized) return '';
  const parts = getJetFractionParts(normalized);
  if (!parts || parts.denominator !== 32) {
    return normalized;
  }
  return `${parts.numerator}/${parts.denominator}`;
};

const showJetSizeFormatError = (toastId: string) => {
  toast.error('Jet size must be entered in 32nds (e.g., 10 or 10/32").', {
    id: toastId
  });
};

const hasImmediateJetSizeError = (value: string, toastId: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const normalized = normalizeJetSizeInput(trimmed);
  if (!normalized) return false;
  if (normalized.endsWith('/')) return false;
  const parts = getJetFractionParts(trimmed);
  if (!parts || parts.denominator !== 32) {
    showJetSizeFormatError(toastId);
    return true;
  }
  return false;
};

const summarizeJets = (rows: JetRow[]) => {
  const counts = new Map<string, number>();

  rows.forEach(row => {
    const rawSize = (row.size || '').trim();
    if (!rawSize) return;

    const parts = getJetFractionParts(rawSize);
    if (!parts || parts.denominator !== 32) return;

    const displaySize = `${parts.numerator}/32"`;

    let count = parseNumber(row.count, 0);
    if (!Number.isFinite(count) || count <= 0) {
      count = 1;
    }
    count = Math.max(1, Math.round(count));

    counts.set(displaySize, (counts.get(displaySize) || 0) + count);
  });

  if (!counts.size) return '';

  const parts = Array.from(counts.entries()).map(([size, count]) => {
    return `${count}@${size}`;
  });

  if (parts.length === 1) {
    return `(Jets: ${parts[0]})`;
  }

  if (parts.length === 2) {
    return `(Jets: ${parts[0]} and ${parts[1]})`;
  }

  const last = parts.pop();
  return `(Jets: ${parts.join(', ')}, and ${last})`;
};

const formatSignificant = (value?: number | null, significantDigits = 2) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  if (value === 0) {
    return Number(0).toLocaleString(undefined, {
      maximumFractionDigits: significantDigits,
      minimumFractionDigits: Math.min(3, significantDigits)
    });
  }

  return value.toLocaleString(undefined, {
    maximumSignificantDigits: significantDigits,
    minimumSignificantDigits: significantDigits
  });
};

const formatFixed = (value?: number | null, fractionDigits = 2) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return value.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits
  });
};

const formatJetImpactForce = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return Math.round(value).toLocaleString();
};

const cloneResultsState = (state: ResultsState): ResultsState => ({
  totalTFA: state.totalTFA,
  scenarios: state.scenarios.map(scenario => ({
    flow: scenario.flow,
    label: scenario.label,
    deltaLabel: scenario.deltaLabel,
    bit: { ...scenario.bit },
    ur: { ...scenario.ur }
  }))
});

const snapshotJetRows = (rows: JetRow[]): JetRowSnapshot[] =>
  rows.map(row => ({ count: row.count, size: row.size }));

const restoreJetRows = (snapshot: JetRowSnapshot[]): JetRow[] => {
  if (!snapshot.length) {
    return [{ id: uid(), count: '', size: '' }];
  }
  return snapshot.map(row => ({ id: uid(), count: row.count, size: row.size }));
};

const summarizeJetSnapshot = (snapshot: JetRowSnapshot[]) => {
  if (!snapshot.length) return '';
  const tempRows: JetRow[] = snapshot.map((row, index) => ({
    id: `snapshot-${index}`,
    count: row.count,
    size: row.size
  }));
  return summarizeJets(tempRows);
};

const formatConstantsSnapshot = (constants: Constants) => {
  const keys: (keyof Constants)[] = ['C1', 'C2', 'C3', 'C4', 'PI'];
  return keys
    .map(key => `${key}:${(constants[key] || '').toString().trim()}`)
    .join(' | ');
};

const normalizeTimelineValue = (value: string) =>
  value.replace(/\s+/g, '').toLowerCase();

const getSnapshotInputsSummary = (
  inputs: CalculatorInputsSnapshot
): TimelineSummaryItem[] => {
  const comparisonRaw = inputs.comparisonRate?.trim() || '';
  const comparisonValue = comparisonRaw
    ? comparisonRaw.endsWith('%')
      ? comparisonRaw
      : `${comparisonRaw}%`
    : '0%';
  const toolModeValue = inputs.isGenericMode ? 'Generic' : 'Tool-specific';
  const toolTypeValue = inputs.isGenericMode
    ? 'Generic'
    : inputs.toolType || 'N/A';
  const toolSeriesValue = inputs.isGenericMode
    ? 'N/A'
    : inputs.toolSeries || 'N/A';
  const bitJetsValue = inputs.bitJetsBlank
    ? 'No bit jets (blank)'
    : summarizeJetSnapshot(inputs.bitJets) || 'None';
  const urJetsValue = inputs.urJetsBlank
    ? 'No UR jets (blank)'
    : summarizeJetSnapshot(inputs.urJets) || 'None';
  const jobValue = inputs.selectedJobLabel?.trim()
    ? inputs.selectedJobLabel
    : 'Unassigned';

  return [
    { key: 'mudWt', label: 'Mud Weight (ppg)', value: inputs.mudWt || '' },
    { key: 'flowRate', label: 'Flow Rate (gpm)', value: inputs.flowRate || '' },
    {
      key: 'comparisonRate',
      label: 'Comparison Rate',
      value: comparisonValue
    },
    {
      key: 'bitDiameter',
      label: 'Bit Diameter (in)',
      value: inputs.bitDiameter
    },
    { key: 'urDiameter', label: 'UR Diameter (in)', value: inputs.urDiameter },
    { key: 'toolMode', label: 'Tool Mode', value: toolModeValue },
    { key: 'toolType', label: 'Tool Type', value: toolTypeValue },
    { key: 'toolSeries', label: 'Tool Series', value: toolSeriesValue },
    { key: 'bitJets', label: 'Bit Jets', value: bitJetsValue },
    { key: 'urJets', label: 'Underreamer Jets', value: urJetsValue },
    { key: 'job', label: 'Job', value: jobValue },
    {
      key: 'constants',
      label: 'Constants',
      value: formatConstantsSnapshot(inputs.constants)
    }
  ];
};

const getTimelineDiff = (
  current: CalculatorInputsSnapshot,
  previous?: CalculatorInputsSnapshot
): TimelineDiffItem[] => {
  const currentSummary = getSnapshotInputsSummary(current);
  const previousSummaryMap = new Map<string, string>();

  if (previous) {
    getSnapshotInputsSummary(previous).forEach(item => {
      previousSummaryMap.set(item.key, item.value);
    });
  }

  return currentSummary.map(item => {
    const previousRaw = previousSummaryMap.get(item.key);
    const previousValue = previousRaw ?? '—';
    const changed = previous
      ? normalizeTimelineValue(item.value || '') !==
        normalizeTimelineValue(previousRaw || '')
      : true;

    return {
      ...item,
      previousValue,
      changed
    };
  });
};

const buildJetErrorsMap = (rows: JetRow[]): JetErrorsMap => {
  return rows.reduce<JetErrorsMap>((acc, row) => {
    acc[row.id] = { count: false, size: false };
    return acc;
  }, {} as JetErrorsMap);
};

const displayOrDash = (value: string) =>
  value && value.trim().length ? value : '—';

const getUserDisplayName = (user: any): string => {
  if (!user) return 'Unknown user';
  const nameParts = [user.firstName, user.lastName]
    .filter(part => typeof part === 'string' && part.trim().length)
    .map(part => part.trim());
  if (nameParts.length) {
    return nameParts.join(' ');
  }
  if (typeof user.email === 'string' && user.email.trim()) {
    return user.email.trim();
  }
  if (typeof user.username === 'string' && user.username.trim()) {
    return user.username.trim();
  }
  if (typeof user._id === 'string' && user._id.trim()) {
    return user._id.trim();
  }
  return 'Unknown user';
};

/**
 * Memoized row item — stable between renders for unchanged rows.
 * Receives stable callbacks and only re-renders when `row` changes.
 */
const JetRowItem = React.memo(function JetRowItem(props: JetRowItemProps) {
  const {
    row,
    index,
    onChangeCount,
    onChangeSize,
    onBlurSize,
    countError,
    sizeError,
    onRemove,
    disableRemove,
    disabled
  } = props;
  return (
    <Row className="mb-2 align-items-center g-2">
      <Col xs="auto" className="fw-semibold text-muted">
        {index + 1}
      </Col>

      <Col xs={6} sm>
        <Form.Control
          type="text"
          inputMode="numeric"
          placeholder="Count"
          value={row.count}
          onChange={e => onChangeCount(row.id, e.target.value)}
          isInvalid={countError}
          disabled={disabled}
          style={{ minWidth: 110 }}
        />
        {countError && (
          <Form.Control.Feedback type="invalid">
            Enter a positive count.
          </Form.Control.Feedback>
        )}
      </Col>

      <Col xs={6} sm>
        <Form.Control
          type="text"
          placeholder="Ex: 10 or 10/32"
          value={row.size}
          onChange={e => onChangeSize(row.id, e.target.value)}
          onBlur={() => onBlurSize?.(row.id)}
          isInvalid={sizeError}
          disabled={disabled}
          style={{ minWidth: 130 }}
        />
        {sizeError && (
          <Form.Control.Feedback type="invalid">
            Enter a jet size like 10 or 10/32".
          </Form.Control.Feedback>
        )}
      </Col>

      {onRemove && (
        <Col xs="auto">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onRemove(row.id)}
            disabled={disableRemove || disabled}
            aria-label={`Remove jet row ${index + 1}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Col>
      )}
    </Row>
  );
});

const PressureDropCalculator: React.FC = () => {
  const [mudWt, setMudWt] = useState('');
  const [flowRate, setFlowRate] = useState('');
  const [comparisonRate, setComparisonRate] = useState('');
  const [bitDiameter, setBitDiameter] = useState('');
  const [urDiameter, setUrDiameter] = useState('');
  const [constants, setConstants] = useState<Constants>(DEFAULT_CONSTANTS);
  const [showConstants, setShowConstants] = useState<boolean>(false);

  const [bitJets, setBitJets] = useState<JetRow[]>([
    { id: uid(), count: '', size: '' }
  ]);
  const [urJets, setUrJets] = useState<JetRow[]>([
    { id: uid(), count: '', size: '' }
  ]);
  const [bitJetsBlank, setBitJetsBlank] = useState<boolean>(false);
  const [urJetsBlank, setUrJetsBlank] = useState<boolean>(false);

  const [results, setResults] = useState<ResultsState>({
    totalTFA: null,
    scenarios: []
  });
  const [lastCalculated, setLastCalculated] = useState<Date | null>(null);
  const [lastCalculatedUser, setLastCalculatedUser] = useState<string>('');
  const { authUser } = useAccessToken();
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();
  const authUserId = getEntityId(authUser) || '';
  const userDisplayName = useMemo(
    () => getUserDisplayName(authUser),
    [authUser]
  );
  const [toolType, setToolType] = useState<
    'BR' | 'DTU' | 'DT' | 'DTS' | 'DDT' | ''
  >('');
  const [toolSeries, setToolSeries] = useState<string>('');
  const [isGenericMode, setIsGenericMode] = useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [isSavingToJob, setIsSavingToJob] = useState<boolean>(false);
  const [jobSearchTerm, setJobSearchTerm] = useState<string>('');
  const [jobSearchQuery, setJobSearchQuery] = useState<string>('');
  const [selectedJobOption, setSelectedJobOption] =
    useState<PressureDropJobOption | null>(null);
  const [filteredJobs, setFilteredJobs] = useState<PressureDropJobOption[]>([]);
  const [bitJetErrors, setBitJetErrors] = useState<JetErrorsMap>({});
  const [urJetErrors, setUrJetErrors] = useState<JetErrorsMap>({});
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [needsRecalc, setNeedsRecalc] = useState<boolean>(false);
  const [parameterAlerts, setParameterAlerts] = useState<string[]>([]);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [activeTimelineIndex, setActiveTimelineIndex] = useState<number | null>(
    null
  );
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState<boolean>(false);
  const [tfaPulse, setTfaPulse] = useState<boolean>(false);
  const [snapshotPulse, setSnapshotPulse] = useState<boolean>(false);
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState<number>(0);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const skipTimelineApplyRef = useRef<boolean>(false);
  const restoringTimelineEntryRef = useRef<boolean>(false);
  const tfaPulseTimeoutRef = useRef<number | null>(null);
  const snapshotPulseTimeoutRef = useRef<number | null>(null);

  const [showShearSection, setShowShearSection] = useState<boolean>(false);
  const [shearScrewCount, setShearScrewCount] = useState<string>('0');
  const [shearTensileStrength, setShearTensileStrength] =
    useState<string>('64600');
  const [shearMinorDiaArea, setShearMinorDiaArea] =
    useState<string>('0.129141');
  const [shearValuePct, setShearValuePct] = useState<string>('57');
  const [shearPsiToMoveArms, setShearPsiToMoveArms] = useState<string>('0');
  const [shearPistonArea, setShearPistonArea] = useState<string>('');
  const [shearSpringUsed, setShearSpringUsed] = useState<boolean>(false);
  const [shearSpringPSI, setShearSpringPSI] = useState<string>('0');

  const microInteractionStyles = useMemo(
    () => `
@keyframes cardPulseRing {
  0% { box-shadow: 0 0 0 0 rgba(32, 142, 250, 0.35); transform: translateY(0); }
  60% { box-shadow: 0 0 0 12px rgba(32, 142, 250, 0); transform: translateY(-2px); }
  100% { box-shadow: 0 0 0 0 rgba(32, 142, 250, 0); transform: translateY(0); }
}
.card-pulse { animation: cardPulseRing 0.7s ease; }

@keyframes snapshotFlash {
  0% { background-color: rgba(255, 255, 255, 0.35); }
  100% { background-color: transparent; }
}
.snapshot-flash { animation: snapshotFlash 0.6s ease; }

.chart-context-wrap { border-radius: 20px; padding: 1px; }
.chart-context-inner { border-radius: 18px; overflow: hidden; }
`,
    []
  );

  const { data: searchResults = [], isLoading: isSearchingJobs } = useQuery<
    Job[],
    unknown,
    PressureDropJobOption[]
  >(
    ['searchJobs', jobSearchQuery],
    async () => {
      if (!jobSearchQuery.trim()) {
        return [];
      }
      try {
        const response = await SEARCH_JOBS(jobSearchQuery);
        const jobs = ((response as any)?.data ?? response ?? []) as Job[];
        if (!Array.isArray(jobs)) {
          return [];
        }
        return jobs;
      } catch (error) {
        console.error('Error searching jobs:', error);
        return [];
      }
    },
    {
      enabled: !!jobSearchQuery.trim(),
      staleTime: 0,
      select: jobs => {
        if (!Array.isArray(jobs)) {
          return [];
        }
        return (jobs || [])
          .filter(job => {
            if (job?.jobStage?.name) {
              const stageName = job.jobStage.name.toLowerCase();
              return stageName !== 'closed' && stageName !== 'cancelled';
            }
            return !!getEntityId(job);
          })
          .map<PressureDropJobOption>(job => ({
            value: getEntityId(job) as string,
            label: job.title,
            jobNumber: job.jobNumber || undefined,
            well: job.well || undefined,
            location:
              typeof job.location === 'string'
                ? job.location
                : job.location?.location || undefined,
            jobTypeName:
              typeof job.jobType?.name === 'string'
                ? job.jobType.name
                : undefined
          }));
      }
    }
  );

  const adaptivePalette = useMemo(() => {
    const paletteKey = isGenericMode ? 'DEFAULT' : toolType || 'DEFAULT';
    const basePalette =
      TOOL_THEME_PRESETS[paletteKey] || TOOL_THEME_PRESETS.DEFAULT;
    const seriesFactor = toolSeries ? Math.min(toolSeries.length / 6, 0.32) : 0;
    const lightenTarget = toolSeries ? basePalette.accent : '#ffffff';

    const applySeries = (color: string, intensity: number) => {
      if (!seriesFactor) return color;
      return mixHexColor(
        color,
        lightenTarget,
        Math.min(1, seriesFactor * intensity)
      );
    };

    const applyTheme = (color: string, darkMix = 0.18) =>
      theme === 'dark' ? mixHexColor(color, '#0f172a', darkMix) : color;

    const transform = (color: string, intensity: number, darkMix = 0.18) =>
      applyTheme(applySeries(color, intensity), darkMix);

    const gradientStartHex = transform(basePalette.gradientStops[0], 0.5, 0.12);
    const gradientEndBase = mixHexColor(
      basePalette.gradientStops[1],
      basePalette.accent,
      0.25 + seriesFactor * 0.4
    );
    const gradientEndHex = applyTheme(gradientEndBase, 0.22);

    return {
      bitPressure: transform(basePalette.bitPressure, 0.9),
      urPressure: transform(basePalette.urPressure, 0.6),
      bitFlow: transform(basePalette.bitFlow, 0.7, 0.12),
      urFlow: transform(basePalette.urFlow, 0.5, 0.12),
      accent: transform(basePalette.accent, 1, 0.2),
      gradientStops: [
        withAlpha(gradientStartHex, theme === 'dark' ? 0.55 : 0.35),
        withAlpha(gradientEndHex, theme === 'dark' ? 0.94 : 0.8)
      ] as [string, string]
    };
  }, [isGenericMode, toolSeries, toolType, theme]);

  const shearToolKey = isGenericMode ? '' : `${toolType} ${toolSeries}`;
  const isShearTool = SHEAR_SCREW_TOOL_KEYS.has(shearToolKey);

  const shearCalc = useMemo(() => {
    const n = parseNumber(shearScrewCount, 0);
    const tensile = parseNumber(shearTensileStrength, 64600);
    const minorArea = parseNumber(shearMinorDiaArea, 0.129141);
    const pct = parseNumber(shearValuePct, 57) / 100;
    const psiArms = parseNumber(shearPsiToMoveArms, 0);
    const piston = parseNumber(shearPistonArea, 0);
    const spring = shearSpringUsed ? parseNumber(shearSpringPSI, 0) : 0;

    if (piston <= 0)
      return { shearPSI: 0, pressureDropToOpen: 0, stayBelow: 0 };

    const shearPSI =
      (tensile * pct * minorArea + piston * psiArms + spring) / piston;
    const pressureDropToOpen = n * shearPSI;
    const stayBelow = pressureDropToOpen * 0.8;

    return {
      shearPSI: round(shearPSI, 2),
      pressureDropToOpen: round(pressureDropToOpen, 2),
      stayBelow: round(stayBelow, 2)
    };
  }, [
    shearScrewCount,
    shearTensileStrength,
    shearMinorDiaArea,
    shearValuePct,
    shearPsiToMoveArms,
    shearPistonArea,
    shearSpringUsed,
    shearSpringPSI
  ]);

  const shearDataForPdf = useMemo(() => {
    const key = isGenericMode ? '' : `${toolType} ${toolSeries}`;
    if (!SHEAR_SCREW_TOOL_KEYS.has(key) || isGenericMode || !shearPistonArea) {
      return null;
    }
    return {
      toolKey: key,
      screwCount: shearScrewCount,
      tensileStrength: shearTensileStrength,
      minorDiaArea: shearMinorDiaArea,
      shearValuePct: shearValuePct,
      psiToMoveArms: shearPsiToMoveArms,
      pistonArea: shearPistonArea,
      springUsed: shearSpringUsed,
      springPSI: shearSpringPSI,
      shearPSI: shearCalc.shearPSI,
      pressureDropToOpen: shearCalc.pressureDropToOpen,
      stayBelow: shearCalc.stayBelow
    };
  }, [
    isGenericMode,
    toolType,
    toolSeries,
    shearPistonArea,
    shearScrewCount,
    shearTensileStrength,
    shearMinorDiaArea,
    shearValuePct,
    shearPsiToMoveArms,
    shearSpringUsed,
    shearSpringPSI,
    shearCalc
  ]);

  const bitJetsSummary = useMemo(() => {
    if (bitJetsBlank) return '(No bit jets)';
    return summarizeJets(bitJets);
  }, [bitJets, bitJetsBlank]);
  const urJetsSummary = useMemo(() => {
    if (urJetsBlank) return '(No UR jets)';
    return summarizeJets(urJets);
  }, [urJets, urJetsBlank]);
  const lastCalculatedText = useMemo(() => {
    if (!lastCalculated) return '';
    const userName = lastCalculatedUser || userDisplayName;
    return `Last calculated by ${userName} on ${lastCalculated}`;
  }, [lastCalculated, lastCalculatedUser, userDisplayName]);
  const scenarioCount = results.scenarios.length;
  const hasConfiguredTool = useMemo(
    () => isGenericMode || (!!toolType && !!toolSeries),
    [isGenericMode, toolSeries, toolType]
  );
  const hasConfiguredJets = useMemo(() => {
    if (bitJetsBlank) {
      return true;
    }
    return bitJets.some(row => {
      const countValue = (row.count || '').trim();
      const sizeValue = (row.size || '').trim();
      if (!countValue || !sizeValue) {
        return false;
      }
      const numericCount = Number(countValue.replace(/,/g, ''));
      const jetDiameter = parseJetDiameterInches(sizeValue);
      return (
        Number.isFinite(numericCount) &&
        numericCount > 0 &&
        jetDiameter !== null
      );
    });
  }, [bitJets, bitJetsBlank]);
  const hasCalculatedOnce = scenarioCount > 0;
  const onboardingSteps = useMemo(() => {
    return [
      {
        id: 'inputs',
        label: 'Choose a tool mode and set baseline inputs',
        complete: hasConfiguredTool
      },
      {
        id: 'jets',
        label: 'Configure at least one bit jet size & count',
        complete: hasConfiguredJets
      },
      {
        id: 'calculate',
        label: 'Run Calculate to generate comparison scenarios',
        complete: hasCalculatedOnce
      }
    ];
  }, [hasCalculatedOnce, hasConfiguredJets, hasConfiguredTool]);
  const onboardingComplete = useMemo(
    () => onboardingSteps.every(step => step.complete),
    [onboardingSteps]
  );
  const formatPressureDisplay = useCallback((value?: number | null) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }, []);
  const formatFlowDisplay = useCallback((value?: number | null) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
    const raw = value.toString();
    const fractionalPart = raw.includes('.')
      ? raw.split('.')[1].replace(/0+$/, '')
      : '';
    const digits =
      fractionalPart.length === 0 ? 0 : Math.min(fractionalPart.length, 2);
    return value.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits === 0 ? 0 : 2
    });
  }, []);
  const resolveThemeColor = useCallback(
    (token: string, fallback: string) => {
      const value = getThemeColor(token);
      return value && value.length ? value : fallback;
    },
    [getThemeColor]
  );
  const bitPressureLineColor = adaptivePalette.bitPressure;
  const urPressureLineColor = adaptivePalette.urPressure;
  const bitFlowLineColor = adaptivePalette.bitFlow;
  const urFlowLineColor = adaptivePalette.urFlow;
  const pressureDropSeries = useMemo<PressureDropSeriesPoint[]>(() => {
    if (!results.scenarios.length) return [];
    return results.scenarios
      .map<PressureDropSeriesPoint | null>((scenario, index) => {
        const flowValue =
          typeof scenario.flow === 'number' && Number.isFinite(scenario.flow)
            ? scenario.flow
            : null;
        const bitPressureValue =
          typeof scenario.bit?.['Pressure Drop (PSI)'] === 'number' &&
          Number.isFinite(scenario.bit['Pressure Drop (PSI)'])
            ? scenario.bit['Pressure Drop (PSI)']
            : null;
        const urPressureValue =
          typeof scenario.ur?.['Pressure Drop (PSI)'] === 'number' &&
          Number.isFinite(scenario.ur['Pressure Drop (PSI)'])
            ? scenario.ur['Pressure Drop (PSI)']
            : null;
        const bitFlowValue =
          typeof scenario.bit?.Flow === 'number' &&
          Number.isFinite(scenario.bit.Flow)
            ? scenario.bit.Flow
            : null;
        const urFlowValue =
          typeof scenario.ur?.Flow === 'number' &&
          Number.isFinite(scenario.ur.Flow)
            ? scenario.ur.Flow
            : null;

        if (
          flowValue === null ||
          bitPressureValue === null ||
          urPressureValue === null ||
          bitFlowValue === null ||
          urFlowValue === null
        ) {
          return null;
        }

        return {
          flow: round(flowValue, 3),
          bitPressureDrop: round(bitPressureValue, 3),
          urPressureDrop: round(urPressureValue, 3),
          deltaLabel: scenario.deltaLabel,
          isBase: scenario.deltaLabel === 'Base',
          bitFlow: round(bitFlowValue, 3),
          urFlow: round(urFlowValue, 3),
          scenarioIndex: index
        };
      })
      .filter((point): point is PressureDropSeriesPoint => point !== null)
      .sort((a, b) => a.flow - b.flow);
  }, [results.scenarios]);
  const pressureCurvesIdentical = useMemo(() => {
    if (!pressureDropSeries.length) return false;
    return pressureDropSeries.every(
      point => Math.abs(point.bitPressureDrop - point.urPressureDrop) <= 1e-6
    );
  }, [pressureDropSeries]);
  const bitPressureDisplayColor = bitPressureLineColor;
  const urPressureDisplayColor = pressureCurvesIdentical
    ? bitPressureLineColor
    : urPressureLineColor;
  const urPressureDisplayOpacity = pressureCurvesIdentical ? 1 : 0.75;
  const chartCardPalette = useMemo(() => {
    const withFallback = (value: string, fallback: string) =>
      value && value.length ? value : fallback;

    if (theme === 'dark') {
      return {
        background: withFallback(getThemeColor('gray-soft'), '#0f111a'),
        text: withFallback(getThemeColor('gray-1100'), '#e3e6ed')
      };
    }

    return {
      background: withFallback(getThemeColor('gray-soft'), '#f5f7fa'),
      text: withFallback(getThemeColor('gray-900'), '#31374a')
    };
  }, [getThemeColor, theme]);
  const pressureDropChartOptions = useMemo(() => {
    if (!pressureDropSeries.length) {
      return null;
    }

    const flows = pressureDropSeries.map(point => point.flow);
    const pressures = pressureDropSeries.flatMap(point => [
      point.bitPressureDrop,
      point.urPressureDrop
    ]);
    const bitFlows = pressureDropSeries.map(point => point.bitFlow);
    const urFlows = pressureDropSeries.map(point => point.urFlow);

    const extendRange = (values: number[]) => {
      if (!values.length) return [0, 1] as const;
      const min = Math.min(...values);
      const max = Math.max(...values);
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return [0, 1] as const;
      }
      if (min === max) {
        const padding = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
        const lower = min - padding;
        const upper = max + padding;
        return [Math.max(lower, 0), upper] as const;
      }
      const span = max - min;
      const padding = span * 0.1;
      const lower = Math.max(min - padding, 0);
      const upper = max + padding;
      return [lower, upper] as const;
    };

    const [flowMin, flowMax] = extendRange(flows);
    const [pressureMin, pressureMax] = extendRange(pressures);
    const [flowAxisMin, flowAxisMax] = extendRange([...bitFlows, ...urFlows]);

    const axisLineColor = resolveThemeColor(
      theme === 'dark' ? 'gray-500' : 'gray-600',
      theme === 'dark' ? '#525b75' : '#6e7891'
    );
    const axisLabelColor = resolveThemeColor(
      theme === 'dark' ? 'gray-1000' : 'gray-800',
      theme === 'dark' ? '#cbd0dd' : '#3e465b'
    );
    const gridLineColor = rgbaColor(
      resolveThemeColor(
        theme === 'dark' ? 'gray-500' : 'gray-600',
        theme === 'dark' ? '#525b75' : '#6e7891'
      ),
      theme === 'dark' ? 0.25 : 0.3
    );
    const tooltipBackground =
      theme === 'dark'
        ? rgbaColor(resolveThemeColor('gray-100', '#222834'), 0.92)
        : 'rgba(255, 255, 255, 0.97)';
    const tooltipBorderColor = resolveThemeColor(
      theme === 'dark' ? 'gray-400' : 'gray-300',
      theme === 'dark' ? '#3e465b' : '#dee2e6'
    );
    const textColor = chartCardPalette.text;
    const backgroundColor = chartCardPalette.background;

    const basePoint =
      pressureDropSeries.find(point => point.isBase) ||
      pressureDropSeries[Math.floor(pressureDropSeries.length / 2)];

    const axisLabelFormatter = (value: number) => formatPressureDisplay(value);

    const bitPressureSeriesData = pressureDropSeries.map(point => ({
      value: [point.flow, point.bitPressureDrop],
      base: point.isBase,
      deltaLabel: point.deltaLabel
    }));
    const urPressureSeriesData = pressureDropSeries.map(point => ({
      value: [point.flow, point.urPressureDrop],
      base: point.isBase,
      deltaLabel: point.deltaLabel
    }));
    const bitFlowSeriesData = pressureDropSeries.map(point => ({
      value: [point.flow, point.bitFlow],
      base: point.isBase
    }));
    const urFlowSeriesData = pressureDropSeries.map(point => ({
      value: [point.flow, point.urFlow],
      base: point.isBase
    }));

    return {
      backgroundColor: 'transparent',
      legend: {
        bottom: 8,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: textColor
        }
      },
      title: {
        text: 'Bit & U/R Pres. Drop vs Flow',
        left: 'left',
        textStyle: {
          color: textColor,
          fontWeight: 600,
          fontSize: 16
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          lineStyle: {
            color: axisLabelColor,
            width: 1
          }
        },
        backgroundColor: tooltipBackground,
        borderColor: tooltipBorderColor,
        textStyle: {
          color: textColor
        },
        formatter: (params: any[]) => {
          if (!params || !params.length) {
            return '';
          }
          const firstParam = params[0];
          const [flowValue] = firstParam.value as number[];
          const baseSeriesPoint = pressureDropSeries[firstParam.dataIndex];
          const deltaText = baseSeriesPoint?.deltaLabel || '';
          const flowLabel = formatFlowDisplay(flowValue);
          const rows = params
            .map(param => {
              const [, valueY] = param.value as number[];
              const isPressure =
                param.seriesName === 'U/R Pressure Drop' ||
                param.seriesName === 'Bit Pressure Drop';
              const formattedValue = isPressure
                ? formatPressureDisplay(valueY)
                : formatFlowDisplay(valueY);
              const unit = isPressure ? 'psi' : 'gpm';
              return `<div style="margin-top:2px;">${param.marker}${param.seriesName}: ${formattedValue} ${unit}</div>`;
            })
            .join('');
          const deltaRow = deltaText
            ? `<div style="opacity:0.7; margin-top:4px;">${deltaText}</div>`
            : '';
          return `
            <div>
              <div style="margin-bottom:4px;">Flow Rate: ${flowLabel} gpm</div>
              ${rows}
              ${deltaRow}
            </div>
          `;
        }
      },
      grid: {
        top: 64,
        left: 70,
        right: 32,
        bottom: 60,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        min: flowMin,
        max: flowMax,
        name: 'Flow Rate (gpm)',
        nameLocation: 'middle',
        nameGap: 36,
        nameTextStyle: {
          color: axisLabelColor,
          fontWeight: 600
        },
        axisLine: {
          lineStyle: {
            color: axisLineColor
          }
        },
        axisTick: {
          lineStyle: {
            color: axisLineColor
          }
        },
        axisLabel: {
          color: axisLabelColor,
          formatter: (value: number) => formatFlowDisplay(value)
        },
        splitLine: {
          lineStyle: {
            color: gridLineColor
          }
        }
      },
      yAxis: [
        {
          type: 'value',
          min: pressureMin,
          max: pressureMax,
          name: 'Bit & U/R Pres. Drop (psi)',
          nameLocation: 'middle',
          nameGap: 64,
          nameTextStyle: {
            color: axisLabelColor,
            fontWeight: 600,
            padding: [0, 0, 0, 28]
          },
          axisLine: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisTick: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisLabel: {
            color: axisLabelColor,
            formatter: axisLabelFormatter
          },
          splitLine: {
            lineStyle: {
              color: gridLineColor
            }
          }
        },
        {
          type: 'value',
          position: 'right',
          min: flowAxisMin,
          max: flowAxisMax,
          name: 'Flow to Bit / U/R (gpm)',
          nameLocation: 'middle',
          nameGap: 60,
          nameTextStyle: {
            color: axisLabelColor,
            fontWeight: 600,
            padding: [0, 28, 0, 0]
          },
          axisLine: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisTick: {
            lineStyle: {
              color: axisLineColor
            }
          },
          axisLabel: {
            color: axisLabelColor,
            formatter: (value: number) => formatFlowDisplay(value)
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          type: 'line',
          name: 'Bit Pressure Drop',
          smooth: false,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: (value: unknown, params: any) =>
            params?.data?.base ? 9 : 6,
          z: 3,
          lineStyle: {
            color: bitPressureDisplayColor,
            width: 2,
            type: 'dashed'
          },
          itemStyle: {
            color: bitPressureDisplayColor,
            borderColor: backgroundColor,
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          },
          data: bitPressureSeriesData,
          markPoint: basePoint
            ? {
                symbol: 'circle',
                symbolSize: 12,
                data: [
                  {
                    coord: [basePoint.flow, basePoint.bitPressureDrop],
                    value: basePoint.deltaLabel || 'Base'
                  }
                ],
                itemStyle: {
                  color: bitPressureDisplayColor
                },
                label: {
                  color: textColor,
                  formatter: basePoint.deltaLabel || 'Base',
                  fontWeight: 600,
                  position: 'top'
                }
              }
            : undefined
        },
        {
          type: 'line',
          name: 'U/R Pressure Drop',
          smooth: false,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: (value: unknown, params: any) =>
            params?.data?.base ? 10 : 6,
          z: 2,
          lineStyle: {
            color: urPressureDisplayColor,
            width: 2,
            opacity: urPressureDisplayOpacity
          },
          itemStyle: {
            color: urPressureDisplayColor,
            borderColor: backgroundColor,
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          },
          data: urPressureSeriesData,
          markPoint: basePoint
            ? {
                symbol: 'circle',
                symbolSize: 12,
                data: [
                  {
                    coord: [basePoint.flow, basePoint.urPressureDrop],
                    value: basePoint.deltaLabel || 'Base'
                  }
                ],
                itemStyle: {
                  color: urPressureDisplayColor
                },
                label: {
                  color: textColor,
                  formatter: basePoint.deltaLabel || 'Base',
                  fontWeight: 600,
                  position: 'top'
                }
              }
            : undefined
        },
        {
          type: 'line',
          name: 'Bit Flow',
          yAxisIndex: 1,
          smooth: false,
          showSymbol: true,
          symbol: 'diamond',
          symbolSize: (value: unknown, params: any) =>
            params?.data?.base ? 9 : 6,
          lineStyle: {
            color: bitFlowLineColor,
            width: 2,
            type: 'dashed'
          },
          itemStyle: {
            color: bitFlowLineColor,
            borderColor: backgroundColor,
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          },
          data: bitFlowSeriesData
        },
        {
          type: 'line',
          name: 'U/R Flow',
          yAxisIndex: 1,
          smooth: false,
          showSymbol: true,
          symbol: 'triangle',
          symbolSize: (value: unknown, params: any) =>
            params?.data?.base ? 9 : 6,
          lineStyle: {
            color: urFlowLineColor,
            width: 2,
            type: 'dotted'
          },
          itemStyle: {
            color: urFlowLineColor,
            borderColor: backgroundColor,
            borderWidth: 2
          },
          emphasis: {
            focus: 'series'
          },
          data: urFlowSeriesData
        }
      ]
    };
  }, [
    bitFlowLineColor,
    bitPressureDisplayColor,
    chartCardPalette.background,
    chartCardPalette.text,
    formatFlowDisplay,
    formatPressureDisplay,
    pressureDropSeries,
    resolveThemeColor,
    theme,
    urFlowLineColor,
    urPressureDisplayColor,
    urPressureDisplayOpacity
  ]);
  useEffect(() => {
    if (!pressureDropSeries.length) {
      return;
    }

    const chart = chartInstanceRef.current;
    if (!chart) {
      return;
    }

    const seriesDataIndex = pressureDropSeries.findIndex(
      point => point.scenarioIndex === selectedScenarioIndex
    );

    if (seriesDataIndex === -1) {
      return;
    }

    const seriesIndices = [0, 1, 2, 3];

    try {
      seriesIndices.forEach(seriesIndex => {
        chart.dispatchAction({ type: 'downplay', seriesIndex });
      });

      seriesIndices.forEach(seriesIndex => {
        chart.dispatchAction({
          type: 'highlight',
          seriesIndex,
          dataIndex: seriesDataIndex
        });
      });

      chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: seriesDataIndex
      });
      chart.dispatchAction({
        type: 'updateAxisPointer',
        seriesIndex: 0,
        dataIndex: seriesDataIndex
      });
    } catch (error) {
      console.warn('Failed to update chart highlight', error);
    }
  }, [pressureDropSeries, selectedScenarioIndex]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current = null;
    };
  }, []);
  const tableHighlightColors = useMemo(
    () =>
      ({
        bit: {
          Flow: bitFlowLineColor,
          'Pressure Drop (PSI)': bitPressureDisplayColor
        },
        ur: {
          Flow: urFlowLineColor,
          'Pressure Drop (PSI)': urPressureDisplayColor
        }
      } as const),
    [
      bitFlowLineColor,
      bitPressureDisplayColor,
      urFlowLineColor,
      urPressureDisplayColor
    ]
  );
  const timelineEntryCount = timelineEntries.length;
  const hasTimelineEntries = timelineEntryCount > 0;
  const activeTimelineEntry =
    activeTimelineIndex !== null
      ? timelineEntries[activeTimelineIndex] || null
      : null;

  const timelineDiffs = useMemo(
    () =>
      timelineEntries.map((entry, index) => {
        const previous = index > 0 ? timelineEntries[index - 1] : undefined;
        return getTimelineDiff(entry.inputs, previous?.inputs);
      }),
    [timelineEntries]
  );

  const activeTimelineDiff = useMemo(() => {
    if (
      activeTimelineEntry &&
      activeTimelineIndex !== null &&
      timelineDiffs[activeTimelineIndex]
    ) {
      return timelineDiffs[activeTimelineIndex];
    }
    return [] as TimelineDiffItem[];
  }, [activeTimelineEntry, activeTimelineIndex, timelineDiffs]);

  const activeTimelineChangedItems = useMemo(
    () => activeTimelineDiff.filter(item => item.changed),
    [activeTimelineDiff]
  );
  const activeResultsForVisuals = activeTimelineEntry?.results ?? results;
  const baseVisualScenario = useMemo(() => {
    const source = activeResultsForVisuals;
    if (!source.scenarios.length) return null;
    return (
      source.scenarios.find(scenario => scenario.deltaLabel === 'Base') ??
      source.scenarios[Math.floor(source.scenarios.length / 2)] ??
      null
    );
  }, [activeResultsForVisuals]);

  const chartContextStyle = useMemo(() => {
    if (!baseVisualScenario) {
      return {
        background: `linear-gradient(135deg, ${adaptivePalette.gradientStops[0]}, ${adaptivePalette.gradientStops[1]})`
      };
    }
    const bitPressure =
      typeof baseVisualScenario.bit?.['Pressure Drop (PSI)'] === 'number'
        ? baseVisualScenario.bit['Pressure Drop (PSI)']
        : 0;
    const urPressure =
      typeof baseVisualScenario.ur?.['Pressure Drop (PSI)'] === 'number'
        ? baseVisualScenario.ur['Pressure Drop (PSI)']
        : 0;
    const flowValue =
      typeof baseVisualScenario.flow === 'number' ? baseVisualScenario.flow : 0;
    const pressureContrast = Math.min(
      1,
      Math.abs(bitPressure - urPressure) / 1800
    );
    const energy = Math.min(1, flowValue / 1200);
    const glow = withAlpha(
      adaptivePalette.accent,
      0.25 + energy * 0.25 + pressureContrast * 0.1
    );
    const gradientStart = adaptivePalette.gradientStops[0];
    const gradientEnd = adaptivePalette.gradientStops[1];
    return {
      background: `radial-gradient(circle at 28% 24%, ${glow}, rgba(255,255,255,0) 58%), linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`
    };
  }, [adaptivePalette, baseVisualScenario]);
  const markNeedsRecalc = useCallback(() => {
    if (scenarioCount > 0) {
      setParameterAlerts([]);
      setNeedsRecalc(true);
    }
  }, [scenarioCount]);

  const evaluateToolLimits = useCallback(
    (totalTFA: number | null, basePressure: number | null) => {
      if (!toolType || !toolSeries) return [] as string[];
      const limitsForType = TOOL_LIMITS[toolType];
      const limits = limitsForType?.[toolSeries];
      if (!limits) return [] as string[];

      const warnings: string[] = [];

      if (typeof totalTFA === 'number') {
        if (totalTFA > limits.maxFlowArea + 1e-3) {
          const formattedTFA = formatSignificant(totalTFA, 3);
          const maxFlow = limits.maxFlowArea.toFixed(2);
          const flowWarning = `Total TFA ${formattedTFA} in² exceeds the ${maxFlow} in² maximum for ${toolType} ${toolSeries}.`;
          warnings.push(flowWarning);
        }
      }

      if (typeof basePressure === 'number') {
        if (
          basePressure < limits.pressureMin - 1e-3 ||
          basePressure > limits.pressureMax + 1e-3
        ) {
          const formattedPressure = formatPressureDisplay(basePressure);
          const pressureWarning = `Pressure drop ${formattedPressure} PSI for ${toolType} ${toolSeries} is outside the ${limits.pressureMin}-${limits.pressureMax} PSI range.`;
          warnings.push(pressureWarning);
        }
      }

      return warnings;
    },
    [formatPressureDisplay, toolSeries, toolType]
  );

  const validateInputs = useCallback(() => {
    const errors: string[] = [];

    const newBitErrors: Record<string, JetRowErrors> = {};
    bitJets.forEach(row => {
      newBitErrors[row.id] = { count: false, size: false };
    });

    const newUrErrors: Record<string, JetRowErrors> = {};
    urJets.forEach(row => {
      newUrErrors[row.id] = { count: false, size: false };
    });

    const validatePositiveNumber = (value: string, label: string) => {
      const trimmed = (value || '').trim();
      if (!trimmed) {
        errors.push(`${label} is required.`);
        return;
      }
      const num = Number(trimmed.replace(/,/g, ''));
      if (!Number.isFinite(num)) {
        errors.push(`${label} must be a number.`);
        return;
      }
      if (num <= 0) {
        errors.push(`${label} must be greater than zero.`);
      }
    };

    if (!isGenericMode) {
      if (!toolType) {
        errors.push('Tool type is required.');
      }

      const allowedSeries = toolType ? TOOL_SERIES_MAP[toolType] : [];
      if (!toolSeries) {
        errors.push('Tool series is required.');
      } else if (toolType && !allowedSeries.includes(toolSeries)) {
        errors.push('Selected tool series is not valid for this tool type.');
      }
    }

    validatePositiveNumber(mudWt, 'Mud Weight');
    validatePositiveNumber(flowRate, 'Flow Rate');
    validatePositiveNumber(bitDiameter, 'Bit Diameter');
    validatePositiveNumber(urDiameter, 'UR Diameter');

    const validateJetGroup = (
      rows: JetRow[],
      target: Record<string, JetRowErrors>,
      label: string,
      required = true
    ) => {
      let anyInput = false;
      let validCount = 0;
      let countInvalid = false;
      let sizeInvalid = false;

      rows.forEach(row => {
        const trimmedCount = (row.count || '').trim();
        const trimmedSize = (row.size || '').trim();
        const entryErrors: JetRowErrors = { count: false, size: false };

        if (!trimmedCount && !trimmedSize) {
          target[row.id] = entryErrors;
          return;
        }

        anyInput = true;

        const countValue = Number(trimmedCount.replace(/,/g, ''));
        if (!trimmedCount || !Number.isFinite(countValue) || countValue <= 0) {
          entryErrors.count = true;
          countInvalid = true;
        }

        const sizeValue = parseJetDiameterInches(trimmedSize);
        if (!trimmedSize || sizeValue === null) {
          entryErrors.size = true;
          sizeInvalid = true;
        }

        if (!entryErrors.count && !entryErrors.size) {
          validCount += 1;
        }

        target[row.id] = entryErrors;
      });

      if (countInvalid) {
        errors.push(`Enter a positive count for each ${label} jet.`);
      }
      if (sizeInvalid) {
        errors.push(
          `Enter a valid size (e.g., 10 or 10/32") for each ${label} jet.`
        );
      }
      if (
        (required && (!anyInput || validCount === 0)) ||
        (!required && anyInput && validCount === 0)
      ) {
        errors.push(
          `Provide at least one valid ${label} jet (count and size).`
        );
      }
    };

    const bitRowsForValidation = bitJetsBlank ? [] : bitJets;
    const urRowsForValidation = urJetsBlank ? [] : urJets;

    validateJetGroup(bitRowsForValidation, newBitErrors, 'Bit', !bitJetsBlank);
    validateJetGroup(urRowsForValidation, newUrErrors, 'Underreamer', false);

    setBitJetErrors(bitJetsBlank ? {} : newBitErrors);
    setUrJetErrors(urJetsBlank ? {} : newUrErrors);
    setFieldErrors(errors);

    return errors.length === 0;
  }, [
    bitJets,
    urJets,
    mudWt,
    flowRate,
    bitDiameter,
    urDiameter,
    toolType,
    toolSeries,
    isGenericMode,
    bitJetsBlank,
    urJetsBlank
  ]);

  const sortJobs = useCallback((jobList: PressureDropJobOption[]) => {
    return [...jobList].sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  useEffect(() => {
    if (bitJetsBlank) {
      setBitJetErrors(prev => (Object.keys(prev).length ? {} : prev));
      return;
    }
    setBitJetErrors(prev => {
      const updated: Record<string, JetRowErrors> = { ...prev };
      let changed = false;
      const ids = new Set<string>();
      bitJets.forEach(row => {
        ids.add(row.id);
        if (!updated[row.id]) {
          updated[row.id] = { count: false, size: false };
          changed = true;
        }
      });
      Object.keys(updated).forEach(id => {
        if (!ids.has(id)) {
          delete updated[id];
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [bitJets, bitJetsBlank]);

  useEffect(() => {
    if (urJetsBlank) {
      setUrJetErrors(prev => (Object.keys(prev).length ? {} : prev));
      return;
    }
    setUrJetErrors(prev => {
      const updated: Record<string, JetRowErrors> = { ...prev };
      let changed = false;
      const ids = new Set<string>();
      urJets.forEach(row => {
        ids.add(row.id);
        if (!updated[row.id]) {
          updated[row.id] = { count: false, size: false };
          changed = true;
        }
      });
      Object.keys(updated).forEach(id => {
        if (!ids.has(id)) {
          delete updated[id];
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [urJets, urJetsBlank]);

  useEffect(() => {
    // Update filteredJobs with search results, but always include selected job if it exists
    const sorted = sortJobs(searchResults);
    // If there's a selected job that's not in search results, add it
    if (
      selectedJobOption &&
      !sorted.some(job => job.value === selectedJobOption.value)
    ) {
      sorted.unshift(selectedJobOption);
    }
    setFilteredJobs(sorted);
  }, [searchResults, selectedJobOption, sortJobs]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const stored = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!stored) {
      setShowOnboarding(true);
    }
  }, []);

  useEffect(() => {
    if (!results.scenarios.length) {
      setSelectedScenarioIndex(0);
      return;
    }

    const baseIndex = results.scenarios.findIndex(
      scenario => scenario.deltaLabel === 'Base'
    );
    const preferredIndex = baseIndex >= 0 ? baseIndex : 0;
    setSelectedScenarioIndex(preferredIndex);
  }, [results.scenarios]);

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setJobSearchQuery(searchTerm);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery]);

  const handleJobSearch = useCallback(
    (inputValue: string) => {
      setJobSearchTerm(inputValue);
      if (!inputValue.trim()) {
        setJobSearchQuery('');
        debouncedSetSearchQuery.cancel();
      } else {
        debouncedSetSearchQuery(inputValue);
      }
    },
    [debouncedSetSearchQuery]
  );

  const recordTimelineEntry = useCallback(
    (
      resultsState: ResultsState,
      warnings: string[],
      generatedAtLabel: string
    ) => {
      const createdAt = new Date();
      const jobMeta =
        selectedJobOption ||
        (selectedJobId
          ? searchResults.find(job => job.value === selectedJobId)
          : undefined);

      const snapshot: CalculatorInputsSnapshot = {
        mudWt,
        flowRate,
        comparisonRate,
        bitDiameter,
        urDiameter,
        bitJets: snapshotJetRows(bitJets),
        urJets: snapshotJetRows(urJets),
        bitJetsBlank,
        urJetsBlank,
        toolType: toolType as 'BR' | 'DTU' | 'DT' | 'DTS' | 'DDT',
        toolSeries,
        isGenericMode,
        constants: { ...constants },
        selectedJobId,
        selectedJobLabel: jobMeta?.label || ''
      };

      const entryTemplate: Omit<TimelineEntry, 'label'> = {
        id: uid(),
        createdAtISO: createdAt.toISOString(),
        createdAtLabel: generatedAtLabel,
        generatedBy: userDisplayName,
        inputs: snapshot,
        results: cloneResultsState(resultsState),
        parameterAlerts: [...warnings]
      };

      skipTimelineApplyRef.current = true;
      setTimelineEntries(prev => {
        const entryIndex = prev.length;
        const entry: TimelineEntry = {
          ...entryTemplate,
          label: `Snapshot ${entryIndex + 1}`
        };
        const updated = [...prev, entry];
        setActiveTimelineIndex(entryIndex);
        return updated;
      });
    },
    [
      bitDiameter,
      bitJets,
      comparisonRate,
      constants,
      flowRate,
      isGenericMode,
      searchResults,
      mudWt,
      selectedJobId,
      toolSeries,
      toolType,
      urDiameter,
      urJets,
      userDisplayName,
      bitJetsBlank,
      urJetsBlank
    ]
  );

  const snapshotPreview = useMemo(() => {
    const sourceEntry = activeTimelineEntry;
    const sourceResults = sourceEntry?.results ?? results;
    if (!sourceResults.scenarios.length) {
      return null;
    }

    const pickScenario = (entryScenarioSet: ScenarioResult[]) =>
      entryScenarioSet.find(s => s.deltaLabel === 'Base') ??
      entryScenarioSet[0] ??
      null;

    const scenario = pickScenario(sourceResults.scenarios);
    if (!scenario) return null;

    const previousEntry =
      sourceEntry && activeTimelineIndex !== null && activeTimelineIndex > 0
        ? timelineEntries[activeTimelineIndex - 1]
        : null;
    const previousScenario = previousEntry
      ? pickScenario(previousEntry.results.scenarios)
      : null;

    const safeNumber = (value?: number | null) =>
      typeof value === 'number' && Number.isFinite(value) ? value : null;

    const flow = safeNumber(scenario.flow);
    const bitPressure = safeNumber(scenario.bit?.['Pressure Drop (PSI)']);
    const urPressure = safeNumber(scenario.ur?.['Pressure Drop (PSI)']);
    const prevFlow = safeNumber(previousScenario?.flow);
    const prevBitPressure = safeNumber(
      previousScenario?.bit?.['Pressure Drop (PSI)']
    );

    return {
      label: sourceEntry ? sourceEntry.label : 'Current scenario',
      createdAt: sourceEntry?.createdAtLabel ?? lastCalculated ?? '',
      flow,
      bitPressure,
      urPressure,
      flowDelta: flow !== null && prevFlow !== null ? flow - prevFlow : null,
      bitPressureDelta:
        bitPressure !== null && prevBitPressure !== null
          ? bitPressure - prevBitPressure
          : null,
      alerts: sourceEntry ? sourceEntry.parameterAlerts : parameterAlerts
    };
  }, [
    activeTimelineEntry,
    activeTimelineIndex,
    timelineEntries,
    results,
    parameterAlerts,
    lastCalculated
  ]);

  const handleDismissOnboarding = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    }
    setShowOnboarding(false);
  }, []);

  const handleShowOnboarding = useCallback(() => {
    setShowOnboarding(true);
  }, []);

  const handleChartReady = useCallback((instance: echarts.ECharts) => {
    chartInstanceRef.current = instance;
  }, []);

  useEffect(() => {
    if (results.totalTFA === null) return;
    if (tfaPulseTimeoutRef.current) {
      window.clearTimeout(tfaPulseTimeoutRef.current);
    }
    setTfaPulse(true);
    tfaPulseTimeoutRef.current = window.setTimeout(() => {
      setTfaPulse(false);
      tfaPulseTimeoutRef.current = null;
    }, 650);
  }, [results.totalTFA]);

  useEffect(() => {
    if (!snapshotPreview) return;
    if (snapshotPulseTimeoutRef.current) {
      window.clearTimeout(snapshotPulseTimeoutRef.current);
    }
    setSnapshotPulse(true);
    snapshotPulseTimeoutRef.current = window.setTimeout(() => {
      setSnapshotPulse(false);
      snapshotPulseTimeoutRef.current = null;
    }, 600);
  }, [
    snapshotPreview?.label,
    snapshotPreview?.flow,
    snapshotPreview?.bitPressure,
    snapshotPreview?.urPressure
  ]);

  useEffect(() => {
    if (!snapshotPreview && snapshotPulse) {
      setSnapshotPulse(false);
    }
  }, [snapshotPreview, snapshotPulse]);

  useEffect(() => {
    return () => {
      if (tfaPulseTimeoutRef.current) {
        window.clearTimeout(tfaPulseTimeoutRef.current);
      }
      if (snapshotPulseTimeoutRef.current) {
        window.clearTimeout(snapshotPulseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasTimelineEntries) {
      setIsTimelineExpanded(false);
    }
  }, [hasTimelineEntries]);

  useEffect(() => {
    if (activeTimelineIndex === null) {
      return;
    }

    const entry = timelineEntries[activeTimelineIndex];
    if (!entry) {
      return;
    }

    if (skipTimelineApplyRef.current) {
      skipTimelineApplyRef.current = false;
      return;
    }

    restoringTimelineEntryRef.current = true;

    const restoredBitRows = restoreJetRows(entry.inputs.bitJets);
    const restoredUrRows = restoreJetRows(entry.inputs.urJets);

    setMudWt(entry.inputs.mudWt);
    setFlowRate(entry.inputs.flowRate);
    setComparisonRate(entry.inputs.comparisonRate);
    setBitDiameter(entry.inputs.bitDiameter);
    setUrDiameter(entry.inputs.urDiameter);
    setIsGenericMode(entry.inputs.isGenericMode);
    setToolType(entry.inputs.toolType);
    setToolSeries(entry.inputs.toolSeries);
    setConstants({ ...entry.inputs.constants });
    setSelectedJobId(entry.inputs.selectedJobId);
    setBitJetsBlank(entry.inputs.bitJetsBlank);
    setUrJetsBlank(entry.inputs.urJetsBlank);
    setBitJets(restoredBitRows);
    setUrJets(restoredUrRows);
    setBitJetErrors(
      entry.inputs.bitJetsBlank ? {} : buildJetErrorsMap(restoredBitRows)
    );
    setUrJetErrors(
      entry.inputs.urJetsBlank ? {} : buildJetErrorsMap(restoredUrRows)
    );
    setResults(cloneResultsState(entry.results));
    setParameterAlerts([...entry.parameterAlerts]);
    setLastCalculated(new Date(entry.createdAtLabel));
    setLastCalculatedUser(entry.generatedBy);
    setNeedsRecalc(false);
    setFieldErrors([]);
    setJobSearchTerm('');
    setJobSearchQuery('');
    setShowConstants(false);

    restoringTimelineEntryRef.current = false;
  }, [activeTimelineIndex, timelineEntries]);

  const availableSeries = useMemo(
    () => (toolType ? TOOL_SERIES_MAP[toolType] : []),
    [toolType]
  );

  const handleGenericModeChange = useCallback(
    (nextIsGeneric: boolean) => {
      setIsGenericMode(nextIsGeneric);
      setFieldErrors([]);
      if (nextIsGeneric) {
        setToolType('');
        setToolSeries('');
        setSelectedJobId('');
        setSelectedJobOption(null);
        setJobSearchTerm('');
        setJobSearchQuery('');
        setFilteredJobs([]);
      }
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleTimelineEntrySelect = useCallback(
    (index: number) => {
      if (index < 0 || index >= timelineEntryCount) {
        return;
      }
      setActiveTimelineIndex(index);
    },
    [timelineEntryCount]
  );

  const handleTimelineStep = useCallback(
    (direction: 'prev' | 'next') => {
      if (!timelineEntryCount) {
        return;
      }
      setActiveTimelineIndex(prev => {
        if (prev === null) {
          return direction === 'prev' ? timelineEntryCount - 1 : 0;
        }
        if (direction === 'prev') {
          return prev > 0 ? prev - 1 : timelineEntryCount - 1;
        }
        return prev < timelineEntryCount - 1 ? prev + 1 : 0;
      });
    },
    [timelineEntryCount]
  );

  const toolTypeError = useMemo(
    () => fieldErrors.some(msg => msg.includes('Tool type')),
    [fieldErrors]
  );
  const toolSeriesError = useMemo(
    () =>
      fieldErrors.some(
        msg =>
          msg.includes('Tool series') ||
          msg.includes('Selected tool series is not available')
      ),
    [fieldErrors]
  );
  const toolSeriesErrorMessage = toolType
    ? 'Select a valid series for the chosen tool type.'
    : 'Tool series is required.';

  const handleToolTypeChange = useCallback(
    (value: string) => {
      const nextType = (value || '') as
        | 'BR'
        | 'DTU'
        | 'DT'
        | 'DTS'
        | 'DDT'
        | '';
      if (isGenericMode) {
        setIsGenericMode(false);
      }
      setToolType(nextType);
      setFieldErrors([]);
      if (nextType) {
        const seriesOptions = TOOL_SERIES_MAP[nextType];
        setToolSeries(seriesOptions[0] || '');
      } else {
        setToolSeries('');
      }
      markNeedsRecalc();
    },
    [isGenericMode, markNeedsRecalc]
  );

  const handleToolSeriesChange = useCallback(
    (value: string) => {
      setToolSeries(value);
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleMudWtChange = useCallback(
    (value: string) => {
      setMudWt(value);
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleFlowRateChange = useCallback(
    (value: string) => {
      setFlowRate(value);
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleComparisonRateChange = useCallback(
    (value: string) => {
      setComparisonRate(value);
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleBitDiameterChange = useCallback(
    (value: string) => {
      setBitDiameter(value);
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleUrDiameterChange = useCallback(
    (value: string) => {
      setUrDiameter(value);
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleConstantChange = useCallback(
    (key: keyof Constants, value: string) => {
      setConstants(prev => ({ ...prev, [key]: value }));
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  useEffect(() => {
    const key = `${toolType} ${toolSeries}`;
    const defaults = SHEAR_SCREW_DEFAULTS[key];
    if (defaults) {
      setShearScrewCount(String(defaults.screws));
      setShearPsiToMoveArms(String(defaults.psiToMoveArms));
      setShearPistonArea(String(defaults.pistonArea));
    } else {
      setShowShearSection(false);
    }
  }, [toolType, toolSeries]);

  useEffect(() => {
    // Keep selected job option in sync with selectedJobId
    // If selectedJobId changes but selectedJobOption doesn't match, try to find it in searchResults
    if (selectedJobId && !selectedJobOption) {
      const found = searchResults.find(job => job.value === selectedJobId);
      if (found) {
        setSelectedJobOption(found);
      }
    } else if (!selectedJobId && selectedJobOption) {
      setSelectedJobOption(null);
    }
  }, [selectedJobId, searchResults, selectedJobOption]);

  // Stable updaters using the prev-state form to avoid stale closures
  const onBitCountChange = useCallback(
    (id: string, value: string) => {
      setBitJets(prev =>
        prev.map(r => (r.id === id ? { ...r, count: value } : r))
      );
      setBitJetErrors(prev => ({
        ...prev,
        [id]: { ...(prev[id] || { count: false, size: false }), count: false }
      }));
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const onBitSizeChange = useCallback(
    (id: string, value: string) => {
      setBitJets(prev =>
        prev.map(r => (r.id === id ? { ...r, size: value } : r))
      );
      setBitJetErrors(prev => ({
        ...prev,
        [id]: { ...(prev[id] || { count: false, size: false }), size: false }
      }));
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const onUrCountChange = useCallback(
    (id: string, value: string) => {
      setUrJets(prev =>
        prev.map(r => (r.id === id ? { ...r, count: value } : r))
      );
      setUrJetErrors(prev => ({
        ...prev,
        [id]: { ...(prev[id] || { count: false, size: false }), count: false }
      }));
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const onUrSizeChange = useCallback(
    (id: string, value: string) => {
      setUrJets(prev =>
        prev.map(r => (r.id === id ? { ...r, size: value } : r))
      );
      setUrJetErrors(prev => ({
        ...prev,
        [id]: { ...(prev[id] || { count: false, size: false }), size: false }
      }));
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const onBitSizeBlur = useCallback(
    (id: string) => {
      const row = bitJets.find(r => r.id === id);
      if (!row) return;
      const formatted = formatJetSizeForInput(row.size);
      if (formatted !== row.size) {
        setBitJets(prev =>
          prev.map(r => (r.id === id ? { ...r, size: formatted } : r))
        );
      }
      const hasError = hasImmediateJetSizeError(
        formatted,
        `bit-jet-size-${id}`
      );
      setBitJetErrors(prev => ({
        ...prev,
        [id]: { ...(prev[id] || { count: false, size: false }), size: hasError }
      }));
    },
    [bitJets]
  );

  const onUrSizeBlur = useCallback(
    (id: string) => {
      const row = urJets.find(r => r.id === id);
      if (!row) return;
      const formatted = formatJetSizeForInput(row.size);
      if (formatted !== row.size) {
        setUrJets(prev =>
          prev.map(r => (r.id === id ? { ...r, size: formatted } : r))
        );
      }
      const hasError = hasImmediateJetSizeError(formatted, `ur-jet-size-${id}`);
      setUrJetErrors(prev => ({
        ...prev,
        [id]: { ...(prev[id] || { count: false, size: false }), size: hasError }
      }));
    },
    [urJets]
  );

  const addBitRow = useCallback(() => {
    if (bitJetsBlank) return;
    const newRow = { id: uid(), count: '', size: '' };
    setBitJets(prev => [...prev, newRow]);
    setBitJetErrors(prev => ({
      ...prev,
      [newRow.id]: { count: false, size: false }
    }));
    setFieldErrors([]);
    markNeedsRecalc();
  }, [bitJetsBlank, markNeedsRecalc]);

  const addUrRow = useCallback(() => {
    if (urJetsBlank) return;
    const newRow = { id: uid(), count: '', size: '' };
    setUrJets(prev => [...prev, newRow]);
    setUrJetErrors(prev => ({
      ...prev,
      [newRow.id]: { count: false, size: false }
    }));
    setFieldErrors([]);
    markNeedsRecalc();
  }, [markNeedsRecalc, urJetsBlank]);

  const removeBitRow = useCallback(
    (id: string) => {
      setBitJets(prev => {
        if (prev.length <= 1) {
          return prev;
        }
        const next = prev.filter(row => row.id !== id);
        return next.length ? next : prev;
      });
      setBitJetErrors(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const removeUrRow = useCallback(
    (id: string) => {
      setUrJets(prev => {
        if (prev.length <= 1) {
          return prev;
        }
        const next = prev.filter(row => row.id !== id);
        return next.length ? next : prev;
      });
      setUrJetErrors(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setFieldErrors([]);
      markNeedsRecalc();
    },
    [markNeedsRecalc]
  );

  const handleBitJetsBlankChange = useCallback(
    (next: boolean) => {
      setBitJetsBlank(next);
      setFieldErrors([]);
      setBitJetErrors(next ? {} : buildJetErrorsMap(bitJets));
      markNeedsRecalc();
    },
    [bitJets, markNeedsRecalc]
  );

  const handleUrJetsBlankChange = useCallback(
    (next: boolean) => {
      setUrJetsBlank(next);
      setFieldErrors([]);
      setUrJetErrors(next ? {} : buildJetErrorsMap(urJets));
      markNeedsRecalc();
    },
    [markNeedsRecalc, urJets]
  );

  // Calculation logic
  const calculateResults = useCallback(() => {
    if (!validateInputs()) {
      return;
    }
    const mw = parseNumber(mudWt);
    const baseFlow = parseNumber(flowRate);
    const compRate = parsePercent(comparisonRate);
    const C1 = parseNumber(constants.C1, 10858);
    const C2 = parseNumber(constants.C2, 1714);
    const C3 = parseNumber(constants.C3, 0.320832);
    const C4 = parseNumber(constants.C4, 0.000516);
    const PI = parseNumber(constants.PI, Math.PI);
    const areaFromDiameterInches = (d: number) => PI * Math.pow(d / 2, 2);

    const bitHoleArea =
      parseNumber(bitDiameter) > 0
        ? areaFromDiameterInches(parseNumber(bitDiameter))
        : 0;
    const urHoleArea =
      parseNumber(urDiameter) > 0
        ? areaFromDiameterInches(parseNumber(urDiameter))
        : 0;

    const sumTFA = (rows: JetRow[]) =>
      rows.reduce((sum, r) => {
        const c = parseNumber(r.count);
        const d = parseJetDiameterInches(r.size);
        return c > 0 && d ? sum + c * areaFromDiameterInches(d) : sum;
      }, 0);

    const activeBitJets = bitJetsBlank ? [] : bitJets;
    const activeUrJets = urJetsBlank ? [] : urJets;
    const TFABit = sumTFA(activeBitJets);
    const TFAUR = sumTFA(activeUrJets);
    const TFATotal = TFABit + TFAUR;

    const dp = (flow: number, TFA: number) =>
      TFA > 0 ? (flow * flow * mw) / (C1 * TFA * TFA) : 0;
    const HHP = (dpVal: number, flow: number) => (dpVal * flow) / C2;
    const vel = (flow: number, TFA: number) =>
      TFA > 0 ? (C3 * flow) / TFA : 0;
    const impact = (flow: number, v: number) => C4 * mw * flow * v;

    const areaRemaining = Math.max(urHoleArea - bitHoleArea, 0);
    const percentFmBit =
      urHoleArea > 0
        ? Math.min(Math.max((bitHoleArea / urHoleArea) * 100, 0), 100)
        : 0;
    const percentFmUR =
      urHoleArea > 0
        ? Math.min(Math.max((areaRemaining / urHoleArea) * 100, 0), 100)
        : 0;

    const multipliers = compRate > 0 && baseFlow > 0 ? [-2, -1, 0, 1, 2] : [0];

    const scenarios = multipliers.map(mult => {
      const totalFlow = Math.max(baseFlow * (1 + mult * compRate), 0);
      const flowToBit =
        TFATotal > 0 && TFABit > 0 ? totalFlow * (TFABit / TFATotal) : 0;
      const flowToUR =
        TFATotal > 0 && TFAUR > 0 ? totalFlow * (TFAUR / TFATotal) : 0;

      const dpBit = dp(flowToBit, TFABit);
      const dpUR = dp(flowToUR, TFAUR);

      const hhpBit = HHP(dpBit, flowToBit);
      const hhpUR = HHP(dpUR, flowToUR);

      const hhpsiBit = bitHoleArea > 0 ? hhpBit / bitHoleArea : 0;
      const effectiveUrArea = areaRemaining > 0 ? areaRemaining : urHoleArea;
      const hhpsiUR = effectiveUrArea > 0 ? hhpUR / effectiveUrArea : 0;

      const vBit = vel(flowToBit, TFABit);
      const vUR = vel(flowToUR, TFAUR);

      const impactBit = impact(flowToBit, vBit);
      const impactUR = impact(flowToUR, vUR);

      const deltaPct = mult * compRate * 100;
      const deltaLabel =
        mult === 0
          ? 'Base'
          : `${deltaPct > 0 ? '+' : ''}${round(deltaPct, 2)}%`;

      return {
        flow: totalFlow,
        label: `${round(totalFlow, 3)} gpm`,
        deltaLabel,
        bit: {
          'TFA (In2)': TFABit,
          '% Flow': totalFlow > 0 ? (flowToBit / totalFlow) * 100 : 0,
          Flow: flowToBit,
          'Hole Area': bitHoleArea,
          '% Fm removed by Bit': percentFmBit,
          'Pressure Drop (PSI)': dpBit,
          HHP: hhpBit,
          HHPSI: hhpsiBit,
          'Jet Velocity': vBit,
          'Jet Impact Force': impactBit
        },
        ur: {
          'TFA (In2)': TFAUR,
          '% Flow': totalFlow > 0 ? (flowToUR / totalFlow) * 100 : 0,
          Flow: flowToUR,
          'Hole Area': urHoleArea,
          '% Fm removed by UR': percentFmUR,
          'Pressure Drop (PSI)': dpUR,
          HHP: hhpUR,
          HHPSI: hhpsiUR,
          'Jet Velocity': vUR,
          'Jet Impact Force': impactUR
        }
      };
    });
    const resultsState: ResultsState = {
      totalTFA: TFATotal,
      scenarios
    };
    setResults(resultsState);

    const generatedAtLabel = new Date().toLocaleString();

    setLastCalculated(new Date(generatedAtLabel));
    setLastCalculatedUser(userDisplayName);
    setNeedsRecalc(false);
    setFieldErrors([]);
    const baseScenario =
      scenarios.find(scenario => scenario.deltaLabel === 'Base') ||
      scenarios[Math.floor(scenarios.length / 2)] ||
      null;
    const baseUrPressure = baseScenario
      ? baseScenario.ur['Pressure Drop (PSI)']
      : null;
    const parameterWarnings = evaluateToolLimits(
      urJetsBlank ? null : TFATotal,
      typeof baseUrPressure === 'number' ? baseUrPressure : null
    );
    setParameterAlerts(parameterWarnings);

    recordTimelineEntry(resultsState, parameterWarnings, generatedAtLabel);
  }, [
    mudWt,
    flowRate,
    comparisonRate,
    constants,
    bitDiameter,
    urDiameter,
    bitJets,
    urJets,
    bitJetsBlank,
    urJetsBlank,
    userDisplayName,
    validateInputs,
    evaluateToolLimits,
    recordTimelineEntry
  ]);

  const resetCalculator = useCallback(() => {
    setMudWt('');
    setFlowRate('');
    setComparisonRate('');
    setBitDiameter('');
    setUrDiameter('');
    setToolType('');
    setToolSeries('');
    setIsGenericMode(false);
    setBitJets([{ id: uid(), count: '', size: '' }]);
    setUrJets([{ id: uid(), count: '', size: '' }]);
    setBitJetsBlank(false);
    setUrJetsBlank(false);
    setConstants({ ...DEFAULT_CONSTANTS });
    setShowConstants(false);
    setResults({ totalTFA: null, scenarios: [] });
    setLastCalculated(null);
    setLastCalculatedUser('');
    setBitJetErrors({});
    setUrJetErrors({});
    setFieldErrors([]);
    setNeedsRecalc(false);
    setTimelineEntries([]);
    setActiveTimelineIndex(null);
    setIsTimelineExpanded(false);
    skipTimelineApplyRef.current = false;
    setShowShearSection(false);
    setShearScrewCount('0');
    setShearTensileStrength('64600');
    setShearMinorDiaArea('0.129141');
    setShearValuePct('57');
    setShearPsiToMoveArms('0');
    setShearPistonArea('');
    setShearSpringUsed(false);
    setShearSpringPSI('0');
  }, []);

  const handleSavePdfToJob = async () => {
    if (isSavingToJob) return;

    if (isGenericMode) {
      toast.error('Generic mode only allows downloading the PDF.');
      return;
    }

    if (!selectedJobId) {
      toast.error('Select a job before saving the PDF.');
      return;
    }

    if (!results.scenarios.length) {
      toast.error('Calculate results before saving to a job.');
      return;
    }

    if (needsRecalc) {
      toast.error('Recalculate before saving to ensure results are current.');
      return;
    }

    if (!authUserId) {
      toast.error('Unable to determine the current user.');
      return;
    }

    const jobMeta =
      selectedJobOption ||
      searchResults.find(job => job.value === selectedJobId);
    const jobTypeName = jobMeta?.jobTypeName || '';
    const expectedPrefix =
      toolType && toolSeries
        ? `${toolType}${toolSeries}`.replace(/[^a-z0-9]/gi, '').toUpperCase()
        : '';
    const jobTypePrefix = jobTypeName
      .split(/x/i)[0]
      .replace(/[^a-z0-9]/gi, '')
      .toUpperCase();

    if (
      expectedPrefix &&
      jobTypePrefix &&
      !jobTypePrefix.startsWith(expectedPrefix)
    ) {
      const proceed = window.confirm(
        `The selected job type "${jobTypeName}" does not match the selected tool (` +
          `${toolType} ${toolSeries}).\nDo you want to save the calculation results anyway?`
      );
      if (!proceed) {
        return;
      }
    }

    try {
      setIsSavingToJob(true);

      const generatedAtLabel = lastCalculated || new Date().toLocaleString();
      const generatedByLabel = lastCalculatedUser || userDisplayName;
      const documentNode = (
        <ResultsPDF
          results={results}
          mudWt={mudWt}
          flowRate={flowRate}
          comparisonRate={comparisonRate}
          bitDiameter={bitDiameter}
          urDiameter={urDiameter}
          generatedAt={generatedAtLabel.toLocaleString()}
          generatedBy={generatedByLabel}
          bitSummary={bitJetsSummary}
          urSummary={urJetsSummary}
          parameterAlerts={parameterAlerts}
          toolType={toolType}
          toolSeries={toolSeries}
          isGenericMode={isGenericMode}
          shearData={shearDataForPdf}
        />
      );

      const pdfBlob = await pdf(documentNode).toBlob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const baseName =
        (jobMeta?.jobNumber && jobMeta.jobNumber.trim()) ||
        jobMeta?.label ||
        'job';
      const slug = baseName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const fileName = `pressure-drop-${slug || 'job'}-${timestamp}.pdf`;

      const file = new File([pdfBlob], fileName, {
        type: 'application/pdf'
      });

      await uploadFileWithJob(file, selectedJobId, authUserId);
      toast.success('Results PDF saved to job files.');

      try {
        await CREATE_JOB_ACTIVITY_LOG({
          jobId: selectedJobId,
          activityType: 'pressure_drop_report',
          description: `${generatedByLabel} saved a pressure drop report`,
          metadata: {
            mudWeight: mudWt,
            flowRate,
            comparisonRate,
            bitDiameter,
            urDiameter,
            bitJets,
            urJets,
            totalTFA: results.totalTFA,
            generatedAt: generatedAtLabel,
            bitSummary: bitJetsSummary,
            urSummary: urJetsSummary,
            toolType,
            toolSeries,
            parameterAlerts,
            shearScrewCalculator: shearDataForPdf
              ? {
                  toolKey: shearDataForPdf.toolKey,
                  numberOfScrews: shearDataForPdf.screwCount,
                  tensileStrength: shearDataForPdf.tensileStrength,
                  minorDiaArea: shearDataForPdf.minorDiaArea,
                  shearValuePct: shearDataForPdf.shearValuePct,
                  psiToMoveArms: shearDataForPdf.psiToMoveArms,
                  pistonArea: shearDataForPdf.pistonArea,
                  springUsed: shearDataForPdf.springUsed,
                  springPSI: shearDataForPdf.springPSI,
                  shearPSI: shearDataForPdf.shearPSI,
                  pressureDropToOpen: shearDataForPdf.pressureDropToOpen,
                  stayBelow: shearDataForPdf.stayBelow
                }
              : null
          }
        });
      } catch (logError) {
        console.error(
          'Failed to record job activity for pressure drop report:',
          logError
        );
      }
    } catch (error) {
      console.error('Failed to save pressure drop PDF to job files:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to save PDF to job files.';
      toast.error(message);
    } finally {
      setIsSavingToJob(false);
    }
  };

  const hasResults = scenarioCount > 0;
  const scenarioHeaders = results.scenarios;

  const formatScenarioValue = (metricKey: string, value?: number | null) => {
    if (metricKey === 'Flow') {
      return formatFlowDisplay(value);
    }
    if (metricKey === 'Pressure Drop (PSI)') {
      return formatPressureDisplay(value);
    }
    if (FIXED_TWO_DECIMAL_METRICS.has(metricKey)) {
      return formatFixed(value);
    }
    if (metricKey === 'Jet Impact Force') {
      return formatJetImpactForce(value);
    }
    return formatSignificant(value, 2);
  };
  const formatTotalTFA = (value?: number | null) => {
    return formatSignificant(value, 3);
  };
  const underreamerHeaderContent = useMemo(() => {
    if (isGenericMode) {
      return <span className="text-muted small">Tool: Generic</span>;
    }

    if (!toolType && !toolSeries) {
      return (
        <span className="text-muted small">Tool Type: N/A | Series: N/A</span>
      );
    }

    return (
      <span className="text-muted small">
        Tool Type: {toolType || 'N/A'} | Series: {toolSeries || 'N/A'}
      </span>
    );
  }, [isGenericMode, toolSeries, toolType]);

  const underreamerColumnStatuses = useMemo(() => {
    if (isGenericMode || urJetsBlank || !toolType || !toolSeries) {
      return {} as Record<number, 'warning' | 'critical'>;
    }

    const limitsForType = TOOL_LIMITS[toolType];
    const limits = limitsForType?.[toolSeries];
    if (!limits) {
      return {} as Record<number, 'warning' | 'critical'>;
    }

    return results.scenarios.reduce<Record<number, 'warning' | 'critical'>>(
      (acc, scenario, index) => {
        const urData = scenario.ur || {};
        const tfa = urData['TFA (In2)'];
        const pressure = urData['Pressure Drop (PSI)'];
        let status: 'critical' | 'warning' | null = null;

        if (typeof tfa === 'number' && Number.isFinite(tfa)) {
          if (tfa >= limits.maxFlowArea - 1e-6) {
            status = 'critical';
          } else if (limits.maxFlowArea > 0) {
            const tfaGap = limits.maxFlowArea - tfa;
            if (tfaGap >= 0 && tfaGap / limits.maxFlowArea <= 0.2) {
              status = 'warning';
            }
          }
        }

        if (typeof pressure === 'number' && Number.isFinite(pressure)) {
          if (
            pressure <= limits.pressureMin + 1e-6 ||
            pressure >= limits.pressureMax - 1e-6
          ) {
            status = 'critical';
          } else {
            const nearMin =
              limits.pressureMin > 0 &&
              pressure > limits.pressureMin &&
              (pressure - limits.pressureMin) / limits.pressureMin <= 0.2;
            const nearMax =
              limits.pressureMax > 0 &&
              pressure < limits.pressureMax &&
              (limits.pressureMax - pressure) / limits.pressureMax <= 0.2;
            if (!status && (nearMin || nearMax)) {
              status = 'warning';
            }
          }
        }

        if (status) {
          acc[index] = status;
        }

        return acc;
      },
      {} as Record<number, 'warning' | 'critical'>
    );
  }, [isGenericMode, results.scenarios, toolSeries, toolType, urJetsBlank]);

  const renderScenarioTable = (
    title: string,
    metrics: readonly MetricDescriptor[],
    key: 'bit' | 'ur',
    summary?: string,
    extraHeaderContent?: React.ReactNode
  ) => {
    const buildFrameClass = (
      status: 'warning' | 'critical' | undefined,
      section: 'header' | 'body',
      isLastRow = false
    ) => {
      if (key !== 'ur' || !status) {
        return '';
      }
      const classes = [
        'ur-column-outline',
        status === 'critical'
          ? 'ur-column-outline-critical'
          : 'ur-column-outline-warning'
      ];
      if (section === 'header') {
        classes.push('ur-column-outline-top');
      }
      if (section === 'body' && isLastRow) {
        classes.push('ur-column-outline-bottom');
      }
      return classes.join(' ');
    };

    const tableClassName =
      key === 'ur'
        ? 'table table-sm mb-0 align-middle ur-results-table'
        : 'table table-sm mb-0 align-middle';

    return (
      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="d-flex align-items-center gap-2 flex-wrap">
            <span>{title}</span>
            {extraHeaderContent}
            {summary && <span className="text-muted small">{summary}</span>}
          </Card.Title>
          <div className="table-responsive">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th scope="col">Metric</th>
                  {scenarioHeaders.map((scenario, columnIdx) => {
                    const isBase = scenario.deltaLabel === 'Base';
                    const columnStatus =
                      key === 'ur'
                        ? underreamerColumnStatuses[columnIdx]
                        : undefined;
                    const frameClass = buildFrameClass(columnStatus, 'header');
                    const headerClassName = frameClass
                      ? `text-nowrap ${frameClass}`
                      : 'text-nowrap';
                    return (
                      <th
                        scope="col"
                        key={`${scenario.label}-${scenario.deltaLabel}`}
                        className={headerClassName}
                      >
                        <div className={isBase ? 'fw-semibold' : undefined}>
                          {scenario.label}
                        </div>
                        <small className="text-muted">
                          {scenario.deltaLabel}
                        </small>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric, rowIdx) => {
                  const highlightColor =
                    metric.key === 'Flow'
                      ? tableHighlightColors[key]?.Flow
                      : metric.key === 'Pressure Drop (PSI)'
                      ? tableHighlightColors[key]?.['Pressure Drop (PSI)']
                      : undefined;
                  const highlightStyle = highlightColor
                    ? {
                        color: highlightColor,
                        fontWeight: 600
                      }
                    : undefined;
                  return (
                    <tr key={metric.key}>
                      <th
                        scope="row"
                        className="text-nowrap"
                        style={highlightStyle}
                      >
                        {metric.label}
                      </th>
                      {scenarioHeaders.map((scenario, columnIdx) => {
                        const columnStatus =
                          key === 'ur'
                            ? underreamerColumnStatuses[columnIdx]
                            : undefined;
                        const frameClass = buildFrameClass(
                          columnStatus,
                          'body',
                          rowIdx === metrics.length - 1
                        );
                        const cellClassName = frameClass
                          ? `text-nowrap ${frameClass}`
                          : 'text-nowrap';
                        const cellStyle =
                          !columnStatus && highlightStyle
                            ? highlightStyle
                            : undefined;
                        return (
                          <td
                            key={`${scenario.label}-${scenario.deltaLabel}-${metric.key}`}
                            className={cellClassName}
                            style={cellStyle}
                          >
                            {formatScenarioValue(
                              metric.key,
                              scenario[key][metric.key]
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    );
  };
  return (
    <Container className="py-3">
      <style dangerouslySetInnerHTML={{ __html: microInteractionStyles }} />
      <h2 className="text-center mb-4">HOC Pressure Drop Calculator</h2>

      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none"
          onClick={handleShowOnboarding}
        >
          <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
          Quick tour
        </Button>
      </div>

      {showOnboarding && (
        <Card className="border-0 shadow-sm mb-4 bg-light">
          <Card.Body>
            <div className="d-flex justify-content-between flex-wrap gap-3 align-items-start">
              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-primary"
                    aria-hidden
                  />
                  <span className="fw-semibold">
                    Step through the essentials to build a great scenario set
                  </span>
                </div>
                <span className="text-muted small">
                  Complete each item below—checks fill in automatically as you
                  work.
                </span>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleDismissOnboarding}
              >
                Got it
              </Button>
            </div>
            <div className="d-flex flex-wrap gap-4 mt-3">
              {onboardingSteps.map(step => (
                <div
                  key={step.id}
                  className="d-flex align-items-start gap-2"
                  aria-live="polite"
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className={`mt-1 ${
                      step.complete ? 'text-success' : 'text-muted'
                    }`}
                    aria-hidden
                  />
                  <div>
                    <div className="fw-semibold small">{step.label}</div>
                    {!step.complete && (
                      <div className="text-muted small">
                        {step.id === 'inputs' &&
                          'Select a tool or switch to generic.'}
                        {step.id === 'jets' &&
                          'Enter at least one jet count & size.'}
                        {step.id === 'calculate' &&
                          'Tap Calculate to generate scenarios.'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-top pt-3 mt-4">
              <div className="fw-semibold text-uppercase small text-muted mb-2">
                Explore the rest of the toolkit
              </div>
              <ul className="mb-0 ps-3 small text-muted">
                <li>
                  Use{' '}
                  <span className="fw-semibold text-body-emphasis">
                    Download PDF
                  </span>{' '}
                  to capture a polished report for sharing once results are
                  ready.
                </li>
                <li>
                  Colored outlines on the{' '}
                  <span className="fw-semibold text-body-emphasis">
                    Underreamer Data
                  </span>{' '}
                  table flag tool limits—yellow means you&apos;re closing in on
                  the allowable flow area or pressure window, red means the
                  scenario is already outside spec.
                </li>
                <li>
                  Attach the report to a job card from the export panel when
                  you&apos;re working in tool-specific mode.
                </li>
                <li>
                  Scrub the{' '}
                  <span className="fw-semibold text-body-emphasis">
                    Change History
                  </span>{' '}
                  timeline to replay every tweak and compare scenarios.
                </li>
              </ul>
            </div>
            {onboardingComplete && (
              <div className="d-flex align-items-center gap-2 mt-3 text-success small fw-semibold">
                <FontAwesomeIcon icon={faCheckCircle} aria-hidden />
                Everything is ready—try stepping through the scenario timeline
                below.
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Inputs */}
      <Row className="mb-3 g-3 align-items-end">
        <Col xs={6} sm="auto" className="d-flex align-items-end">
          <Form.Group className="d-flex flex-column align-items-center align-items-sm-start gap-1">
            <Form.Label className="mb-0 small text-uppercase text-muted">
              Tool Mode
            </Form.Label>
            <div className="d-flex align-items-center gap-3">
              <Form.Check
                inline
                type="radio"
                id="pressure-drop-mode-generic"
                name="pressure-drop-tool-mode"
                label="Generic"
                checked={isGenericMode}
                onChange={() => handleGenericModeChange(true)}
                className="mb-0"
                aria-label="Select generic tool mode"
              />
              <Form.Check
                inline
                type="radio"
                id="pressure-drop-mode-tool-specific"
                name="pressure-drop-tool-mode"
                label="Select Tool"
                checked={!isGenericMode}
                onChange={() => handleGenericModeChange(false)}
                className="mb-0"
                aria-label="Select tool-specific mode"
              />
            </div>
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label>{isGenericMode ? 'Tool Type' : 'Tool Type*'}</Form.Label>
          <Form.Select
            value={toolType}
            onChange={e => handleToolTypeChange(e.target.value)}
            isInvalid={toolTypeError}
            disabled={isGenericMode}
          >
            <option value="">Select tool type</option>
            {TOOL_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          {toolTypeError && (
            <Form.Control.Feedback type="invalid">
              Tool type is required.
            </Form.Control.Feedback>
          )}
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label>
            {isGenericMode ? 'Tool Series' : 'Tool Series*'}
          </Form.Label>
          <Form.Select
            value={toolSeries}
            onChange={e => handleToolSeriesChange(e.target.value)}
            disabled={
              isGenericMode || !toolType || availableSeries.length === 1
            }
            isInvalid={toolSeriesError}
          >
            {!toolType && <option value="">Select tool type first</option>}
            {toolType && availableSeries.length > 1 && (
              <option value="">Select tool series</option>
            )}
            {toolType &&
              availableSeries.map(series => (
                <option key={series} value={series}>
                  {series}
                </option>
              ))}
            {toolType && availableSeries.length === 1 && (
              <option value={availableSeries[0]}>{availableSeries[0]}</option>
            )}
          </Form.Select>
          {toolSeriesError && (
            <Form.Control.Feedback type="invalid">
              {toolSeriesErrorMessage}
            </Form.Control.Feedback>
          )}
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label>Mud Weight (ppg)</Form.Label>
          <Form.Control
            value={mudWt}
            onChange={e => handleMudWtChange(e.target.value)}
          />
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label>Flow Rate (gpm)</Form.Label>
          <Form.Control
            value={flowRate}
            onChange={e => handleFlowRateChange(e.target.value)}
          />
        </Col>
        <Col xs={12} sm={6} md={2}>
          <Form.Label>Comparison Rate (%)</Form.Label>
          <Form.Control
            placeholder="e.g. 5%, 10%, 12%"
            value={comparisonRate}
            onChange={e => handleComparisonRateChange(e.target.value)}
          />
        </Col>
      </Row>
      <Row className="mb-3 g-3">
        <Col xs={12} md={6}>
          <Form.Label>Bit Diameter (in)</Form.Label>
          <Form.Control
            value={bitDiameter}
            onChange={e => handleBitDiameterChange(e.target.value)}
          />
        </Col>
        <Col xs={12} md={6}>
          <Form.Label>UR Diameter (in)</Form.Label>
          <Form.Control
            value={urDiameter}
            onChange={e => handleUrDiameterChange(e.target.value)}
          />
        </Col>
      </Row>

      {/* Shear Screw Calculator */}
      {isShearTool && !isGenericMode && (
        <div className="mb-3">
          <Button
            variant={showShearSection ? 'secondary' : 'outline-secondary'}
            size="sm"
            onClick={() => setShowShearSection(prev => !prev)}
          >
            <FontAwesomeIcon
              icon={showShearSection ? faChevronUp : faChevronDown}
              className="me-2"
            />
            Shear Screw Calculator
          </Button>
          <Collapse in={showShearSection}>
            <div>
              <Card className="mt-2 border-warning">
                <Card.Body>
                  <Card.Title className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        color: theme === 'dark' ? '#ffc107' : '#856404'
                      }}
                    >
                      Brass Shear Screw Calculator
                    </span>
                    <span className="text-muted small fw-normal">
                      {shearToolKey}
                    </span>
                  </Card.Title>

                  <Row className="g-3 mb-3">
                    <Col xs={12} sm={6} md={4} lg={2}>
                      <Form.Label>Number of Shear Screws</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={shearScrewCount}
                        onChange={e => setShearScrewCount(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                      <Form.Label>
                        Tensile Strength of Shear Screw (psi)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={shearTensileStrength}
                        onChange={e => setShearTensileStrength(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                      <Form.Label>
                        Minor Diameter Area of Screw (in²)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.000001"
                        value={shearMinorDiaArea}
                        onChange={e => setShearMinorDiaArea(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={2}>
                      <Form.Label>Shear Value (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        value={shearValuePct}
                        onChange={e => setShearValuePct(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                      <Form.Label>PSI to Move Arms</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={shearPsiToMoveArms}
                        onChange={e => setShearPsiToMoveArms(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                      <Form.Label>Area of Piston (in²)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.001"
                        value={shearPistonArea}
                        onChange={e => setShearPistonArea(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={2}>
                      <Form.Label>Spring Used?</Form.Label>
                      <div className="d-flex gap-3 mt-1">
                        <Form.Check
                          type="radio"
                          id="shear-spring-no"
                          label="No"
                          name="shear-spring-used"
                          checked={!shearSpringUsed}
                          onChange={() => setShearSpringUsed(false)}
                        />
                        <Form.Check
                          type="radio"
                          id="shear-spring-yes"
                          label="Yes"
                          name="shear-spring-used"
                          checked={shearSpringUsed}
                          onChange={() => setShearSpringUsed(true)}
                        />
                      </div>
                    </Col>
                    {shearSpringUsed && (
                      <Col xs={12} sm={6} md={4} lg={2}>
                        <Form.Label>Spring PSI Value</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={shearSpringPSI}
                          onChange={e => setShearSpringPSI(e.target.value)}
                        />
                      </Col>
                    )}
                  </Row>

                  <Row className="g-3">
                    <Col xs={12} sm={4}>
                      <div className="border rounded p-3 text-center">
                        <div className="text-muted small text-uppercase fw-semibold mb-1">
                          Shear PSI
                        </div>
                        <div className="fw-bold fs-5">
                          {shearCalc.shearPSI.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={4}>
                      <div className="border rounded p-3 text-center">
                        <div className="text-muted small text-uppercase fw-semibold mb-1">
                          Pressure Drop to Open (psi)
                        </div>
                        <div className="fw-bold fs-5">
                          {shearCalc.pressureDropToOpen.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} sm={4}>
                      <div
                        className="border border-warning rounded p-3 text-center"
                        style={{
                          backgroundColor:
                            theme === 'dark'
                              ? 'rgba(255, 193, 7, 0.1)'
                              : 'rgba(255, 193, 7, 0.12)'
                        }}
                      >
                        <div
                          className="small text-uppercase fw-semibold mb-1"
                          style={{
                            color: theme === 'dark' ? '#ffc107' : '#856404'
                          }}
                        >
                          Stay Below to Drill Out (psi)
                        </div>
                        <div
                          className="fw-bold fs-5"
                          style={{
                            color: theme === 'dark' ? '#ffc107' : '#856404'
                          }}
                        >
                          {shearCalc.stayBelow.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>
                        <div
                          className="small mt-1"
                          style={{
                            color: theme === 'dark' ? '#ffc107' : '#856404'
                          }}
                        >
                          Stay below this pressure when drilling out shear
                          screws
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          </Collapse>
        </div>
      )}

      {fieldErrors.length > 0 && (
        <Alert variant="danger" className="mb-3">
          <ul className="mb-0 ps-3">
            {fieldErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Jet Editors */}
      <Row>
        <Col>
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <Card.Title className="mb-0">Bit Jets</Card.Title>
                <Form.Check
                  type="switch"
                  id="bit-jets-blank"
                  label="No bit jets"
                  checked={bitJetsBlank}
                  onChange={e => handleBitJetsBlankChange(e.target.checked)}
                />
              </div>
              {bitJetsBlank && (
                <div className="text-muted small mb-2">
                  Bit jets are marked blank. Counts and sizes are disabled.
                </div>
              )}
              {bitJets.map((r, i) => (
                <JetRowItem
                  key={r.id}
                  row={r}
                  index={i}
                  onChangeCount={onBitCountChange}
                  onChangeSize={onBitSizeChange}
                  onBlurSize={onBitSizeBlur}
                  countError={bitJetErrors[r.id]?.count}
                  sizeError={bitJetErrors[r.id]?.size}
                  onRemove={removeBitRow}
                  disableRemove={bitJets.length <= 1 || bitJetsBlank}
                  disabled={bitJetsBlank}
                />
              ))}
              <div className="d-flex justify-content-end mt-2">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={addBitRow}
                  disabled={bitJetsBlank}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col>
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <Card.Title className="mb-0">Underreamer Jets</Card.Title>
                <Form.Check
                  type="switch"
                  id="ur-jets-blank"
                  label="No UR jets"
                  checked={urJetsBlank}
                  onChange={e => handleUrJetsBlankChange(e.target.checked)}
                />
              </div>
              {urJetsBlank && (
                <div className="text-muted small mb-2">
                  Underreamer jets are marked blank. Counts and sizes are
                  disabled.
                </div>
              )}
              {urJets.map((r, i) => (
                <JetRowItem
                  key={r.id}
                  row={r}
                  index={i}
                  onChangeCount={onUrCountChange}
                  onChangeSize={onUrSizeChange}
                  onBlurSize={onUrSizeBlur}
                  countError={urJetErrors[r.id]?.count}
                  sizeError={urJetErrors[r.id]?.size}
                  onRemove={removeUrRow}
                  disableRemove={urJets.length <= 1 || urJetsBlank}
                  disabled={urJetsBlank}
                />
              ))}
              <div className="d-flex justify-content-end mt-2">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={addUrRow}
                  disabled={urJetsBlank}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actions */}
      <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Button onClick={calculateResults} variant="primary">
            Calculate
          </Button>
          <Button onClick={resetCalculator} variant="outline-secondary">
            <FontAwesomeIcon icon={faUndo} /> Reset
          </Button>
          {hasResults && (
            <PDFDownloadLink
              document={
                <ResultsPDF
                  results={results}
                  mudWt={mudWt}
                  flowRate={flowRate}
                  comparisonRate={comparisonRate}
                  bitDiameter={bitDiameter}
                  urDiameter={urDiameter}
                  generatedAt={
                    lastCalculated?.toLocaleString() ||
                    new Date().toLocaleString()
                  }
                  generatedBy={lastCalculatedUser || userDisplayName}
                  bitSummary={bitJetsSummary}
                  urSummary={urJetsSummary}
                  parameterAlerts={parameterAlerts}
                  toolType={toolType}
                  toolSeries={toolSeries}
                  isGenericMode={isGenericMode}
                  shearData={shearDataForPdf}
                />
              }
              fileName="pressure-drop-results.pdf"
            >
              {({ loading }) => (
                <Button variant="success" disabled={loading || needsRecalc}>
                  <FontAwesomeIcon icon={faDownload} />{' '}
                  {loading
                    ? 'Generating PDF...'
                    : needsRecalc
                    ? 'Recalculate to Download'
                    : 'Download PDF'}
                </Button>
              )}
            </PDFDownloadLink>
          )}
        </div>

        {hasResults && (
          <div
            className="d-flex flex-column gap-2 ms-lg-auto"
            style={{ minWidth: '280px' }}
          >
            <div className="text-uppercase small fw-semibold text-muted">
              Export & sharing
            </div>
            <div className="bg-light border rounded-2 p-3 d-flex flex-column flex-sm-row align-items-stretch align-items-sm-end gap-3">
              <div className="flex-grow-1 d-flex flex-column flex-sm-row align-items-stretch align-items-sm-end gap-3">
                <div className="flex-grow-1" style={{ minWidth: '260px' }}>
                  <Form.Label className="mb-1 text-uppercase fw-semibold">
                    Save results to job card attachments
                  </Form.Label>
                  <Select
                    options={filteredJobs}
                    value={selectedJobOption}
                    onChange={option => {
                      if (option) {
                        setSelectedJobId(option.value);
                        setSelectedJobOption(option);
                      } else {
                        setSelectedJobId('');
                        setSelectedJobOption(null);
                      }
                      setJobSearchTerm('');
                      setJobSearchQuery('');
                    }}
                    onInputChange={(input, meta) => {
                      if (meta.action === 'input-change') {
                        handleJobSearch(input);
                      }
                    }}
                    onMenuClose={() => {
                      setJobSearchTerm('');
                      setJobSearchQuery('');
                    }}
                    inputValue={jobSearchTerm}
                    isSearchable
                    isLoading={isSearchingJobs}
                    isDisabled={isSavingToJob || isGenericMode}
                    placeholder={
                      isGenericMode
                        ? 'Unavailable in generic mode'
                        : isSearchingJobs
                        ? 'Searching...'
                        : 'Search jobs by name or number'
                    }
                    filterOption={(option, inputValue) => {
                      const searchTerm = inputValue.toLowerCase();
                      const jobData = option.data as PressureDropJobOption;

                      const matchesLabel = jobData.label
                        .toLowerCase()
                        .includes(searchTerm);

                      const matchesJobNumber = jobData.jobNumber
                        ? jobData.jobNumber
                            .toString()
                            .toLowerCase()
                            .includes(searchTerm)
                        : false;

                      const matchesWell = jobData.well
                        ? jobData.well.toLowerCase().includes(searchTerm)
                        : false;

                      return matchesLabel || matchesJobNumber || matchesWell;
                    }}
                    formatOptionLabel={(option: PressureDropJobOption) => (
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center gap-2">
                          <div className="fw-bold">{option.label}</div>
                          {option.jobNumber && (
                            <span className="badge bg-primary text-white small">
                              #{option.jobNumber}
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 small text-muted">
                          {option.well && <span>Well: {option.well}</span>}
                          {option.location && (
                            <span>
                              Location:{' '}
                              {typeof option.location === 'string'
                                ? option.location
                                : option.location.location || 'N/A'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    className="job-select"
                    classNamePrefix="job-select"
                    styles={{
                      control: baseStyles => ({
                        ...baseStyles,
                        backgroundColor: baseStyles.backgroundColor,
                        borderColor: '#dee2e6'
                      }),
                      menu: baseStyles => ({
                        ...baseStyles,
                        backgroundColor: 'white',
                        zIndex: 1000
                      }),
                      option: (baseStyles, state) => ({
                        ...baseStyles,
                        backgroundColor: state.isFocused ? '#f8f9fa' : 'white',
                        color: 'black',
                        padding: '10px'
                      }),
                      singleValue: baseStyles => ({
                        ...baseStyles,
                        color: 'black'
                      }),
                      input: baseStyles => ({
                        ...baseStyles,
                        color: 'black'
                      })
                    }}
                  />
                </div>
                <Button
                  variant="outline-success"
                  onClick={handleSavePdfToJob}
                  disabled={isSavingToJob || !selectedJobId || isGenericMode}
                  className="align-self-end"
                >
                  <FontAwesomeIcon icon={faSave} className="me-1" />
                  {isSavingToJob
                    ? 'Saving...'
                    : 'Save Results to Job Card Attachments'}
                </Button>
                {isGenericMode && (
                  <div className="text-muted small">
                    Generic mode allows downloading only. Select a tool to
                    enable job saves.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {hasResults && needsRecalc && (
        <Alert
          variant="warning"
          className="mb-3 border border-warning-subtle bg-warning-subtle text-warning-emphasis d-flex align-items-center gap-2 shadow-sm"
        >
          <FontAwesomeIcon icon={faInfoCircle} className="fs-5" />
          <div className="d-flex flex-column">
            <span className="fw-semibold">Recalculation recommended</span>
            <span className="small mb-0">
              Inputs changed since the last calculation. Click Calculate to
              refresh the results.
            </span>
          </div>
        </Alert>
      )}

      {parameterAlerts.length > 0 && (
        <Alert variant="danger" className="mb-3">
          <div className="fw-semibold mb-1">Outside acceptable parameters</div>
          <ul className="mb-0 ps-3">
            {parameterAlerts.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Results */}
      {hasResults && results.totalTFA !== null && (
        <Row className="mb-3">
          <Col xs={12}>
            <div className="position-relative d-flex justify-content-center pb-2">
              <Card
                className={`border-0 shadow-sm text-center ${
                  tfaPulse ? 'card-pulse' : ''
                }`}
                style={{ minWidth: '220px' }}
              >
                <Card.Body className="py-3">
                  <Card.Subtitle className="text-muted mb-1">
                    Total TFA (In2)
                  </Card.Subtitle>
                  <div className="fw-semibold" style={{ fontSize: '1.2rem' }}>
                    {formatTotalTFA(results.totalTFA)}
                  </div>
                </Card.Body>
              </Card>
              {lastCalculatedText && (
                <div className="text-muted small position-absolute end-0 bottom-0 text-end">
                  {lastCalculatedText}
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      {hasResults && (
        <Row className="g-3">
          <Col>
            {renderScenarioTable(
              'Bit Data',
              BIT_METRICS,
              'bit',
              bitJetsSummary
            )}
          </Col>
          <Col>
            {renderScenarioTable(
              'Underreamer Data',
              UR_METRICS,
              'ur',
              urJetsSummary,
              underreamerHeaderContent
            )}
          </Col>
        </Row>
      )}

      {hasResults && pressureDropChartOptions && (
        <Row className="mt-4">
          <Col>
            <div className="chart-context-wrap mb-4" style={chartContextStyle}>
              <div className="chart-context-inner">
                <Card
                  className="border-0 shadow-sm"
                  style={{
                    backgroundColor: chartCardPalette.background,
                    color: chartCardPalette.text
                  }}
                >
                  <Card.Body>
                    <ReactEChartsCore
                      echarts={echarts}
                      option={pressureDropChartOptions}
                      notMerge
                      lazyUpdate
                      onChartReady={handleChartReady}
                      style={{ height: 360, width: '100%' }}
                    />
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {snapshotPreview && (
        <div className="d-flex justify-content-center mb-3">
          <Card
            className={`border-0 shadow-sm snapshot-card ${
              snapshotPulse ? 'snapshot-flash' : ''
            }`}
            style={{
              maxWidth: 420,
              width: '100%',
              borderTop: `3px solid ${adaptivePalette.accent}`
            }}
          >
            <Card.Body className="py-3">
              <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                <div>
                  <div className="text-uppercase small text-muted fw-semibold">
                    {snapshotPreview.label}
                  </div>
                  {snapshotPreview.createdAt && (
                    <div className="small text-muted">
                      {snapshotPreview.createdAt.toLocaleString()}
                    </div>
                  )}
                </div>
                {snapshotPreview.flowDelta !== null && (
                  <span
                    className={`badge ${
                      snapshotPreview.flowDelta >= 0
                        ? 'bg-success-subtle text-success'
                        : 'bg-danger-subtle text-danger'
                    }`}
                  >
                    {snapshotPreview.flowDelta >= 0 ? '+' : ''}
                    {formatFlowDisplay(snapshotPreview.flowDelta)} gpm
                  </span>
                )}
              </div>
              <div className="d-flex flex-wrap gap-3">
                <div>
                  <div className="text-uppercase small text-muted">Flow</div>
                  <div className="fw-semibold">
                    {formatFlowDisplay(snapshotPreview.flow)} gpm
                  </div>
                </div>
                <div>
                  <div className="text-uppercase small text-muted">Bit ΔP</div>
                  <div className="fw-semibold">
                    {formatPressureDisplay(snapshotPreview.bitPressure)} psi
                  </div>
                </div>
                <div>
                  <div className="text-uppercase small text-muted">UR ΔP</div>
                  <div className="fw-semibold">
                    {formatPressureDisplay(snapshotPreview.urPressure)} psi
                  </div>
                </div>
              </div>
              {snapshotPreview.alerts.length > 0 && (
                <div className="mt-2 small text-danger d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>{snapshotPreview.alerts[0]}</span>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      {hasTimelineEntries && activeTimelineEntry && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-2">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-primary"
                  aria-hidden
                />
                <span className="fw-semibold">Change history</span>
                <span className="badge bg-light text-body-secondary">
                  {activeTimelineEntry.label}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleTimelineStep('prev')}
                    disabled={timelineEntryCount <= 1}
                    aria-label="Previous change"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleTimelineStep('next')}
                    disabled={timelineEntryCount <= 1}
                    aria-label="Next change"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
                <Button
                  variant={
                    isTimelineExpanded ? 'outline-secondary' : 'outline-primary'
                  }
                  size="sm"
                  onClick={() => setIsTimelineExpanded(prev => !prev)}
                  aria-expanded={isTimelineExpanded}
                  aria-controls="timeline-details"
                >
                  {isTimelineExpanded ? 'Hide details' : 'Show details'}
                </Button>
              </div>
            </div>

            <div className="position-relative my-2" style={{ height: '24px' }}>
              <div
                className="position-absolute top-50 start-0 end-0 bg-secondary opacity-25"
                style={{ height: 2, transform: 'translateY(-50%)' }}
              />
              {timelineEntries.map((entry, index) => {
                const percent =
                  timelineEntryCount <= 1
                    ? 50
                    : (index / (timelineEntryCount - 1)) * 100;
                const isSelected = index === activeTimelineIndex;
                return (
                  <button
                    type="button"
                    key={`${entry.id}-marker`}
                    onClick={() => handleTimelineEntrySelect(index)}
                    className={`position-absolute translate-middle rounded-circle border-0 ${
                      isSelected ? 'bg-primary' : 'bg-secondary'
                    }`}
                    style={{
                      left: `${percent}%`,
                      top: '50%',
                      width: isSelected ? 14 : 10,
                      height: isSelected ? 14 : 10,
                      boxShadow: isSelected
                        ? '0 0 0 4px rgba(13,110,253,0.2)'
                        : 'none'
                    }}
                    aria-label={`Select ${entry.label}`}
                  />
                );
              })}
            </div>
            <div className="d-flex justify-content-between text-muted small mb-1">
              <span>{timelineEntries[0]?.label}</span>
              <span>
                {timelineEntryCount > 1
                  ? timelineEntries[timelineEntryCount - 1]?.label
                  : ''}
              </span>
            </div>

            <Collapse in={isTimelineExpanded}>
              <div id="timeline-details" className="mt-3">
                <div
                  className="d-flex overflow-auto gap-3 pb-2"
                  role="tablist"
                  aria-label="Detailed change history"
                >
                  {timelineEntries.map((entry, index) => {
                    const isSelected = index === activeTimelineIndex;
                    const entryDiff = timelineDiffs[index] || [];
                    const changedItems = entryDiff.filter(item => item.changed);
                    const highlightLines = changedItems
                      .slice(0, 2)
                      .map(
                        item =>
                          `${item.label}: ${displayOrDash(
                            item.previousValue
                          )} -> ${displayOrDash(item.value)}`
                      );
                    const extraCount = Math.max(
                      changedItems.length - highlightLines.length,
                      0
                    );

                    return (
                      <button
                        type="button"
                        key={entry.id}
                        onClick={() => handleTimelineEntrySelect(index)}
                        className={[
                          'border-0 rounded-4 px-3 py-2 text-start shadow-sm flex-shrink-0',
                          isSelected ? 'bg-primary text-white' : 'bg-white'
                        ].join(' ')}
                        style={{
                          minWidth: 180,
                          transition:
                            'transform 0.2s ease, box-shadow 0.2s ease',
                          transform: isSelected ? 'translateY(-4px)' : 'none'
                        }}
                        role="tab"
                        aria-selected={isSelected}
                      >
                        <div className="fw-semibold">{entry.label}</div>
                        <div
                          className={`small ${
                            isSelected ? 'text-white-50' : 'text-muted'
                          }`}
                        >
                          {entry.createdAtLabel}
                        </div>
                        <div
                          className={`small mt-1 ${
                            isSelected ? 'text-white' : 'text-muted'
                          }`}
                        >
                          {highlightLines.length
                            ? highlightLines.join(' | ')
                            : 'No changes'}
                          {extraCount > 0 ? ` | +${extraCount} more` : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 small text-muted">
                  <div className="text-uppercase fw-semibold text-body-secondary mb-2">
                    Changed inputs
                  </div>
                  {activeTimelineChangedItems.length > 0 ? (
                    <ul className="mb-3 ps-3">
                      {activeTimelineChangedItems.map(item => (
                        <li key={item.key}>
                          {item.label}: {displayOrDash(item.previousValue)}
                          {' -> '}
                          {displayOrDash(item.value)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div>No changes from the previous snapshot.</div>
                  )}

                  <div className="text-uppercase fw-semibold text-body-secondary mb-2">
                    Snapshot info
                  </div>
                  <div className="d-flex flex-wrap gap-3">
                    <span>Saved: {activeTimelineEntry.createdAtLabel}</span>
                    <span>
                      By: {displayOrDash(activeTimelineEntry.generatedBy)}
                    </span>
                    <span>
                      Job:{' '}
                      {displayOrDash(
                        activeTimelineEntry.inputs.selectedJobLabel
                      )}
                    </span>
                  </div>

                  {activeTimelineEntry.parameterAlerts.length > 0 && (
                    <div className="mt-3">
                      <div className="text-uppercase fw-semibold text-danger mb-1">
                        Parameter alerts
                      </div>
                      <ul className="mb-0 ps-3 text-danger">
                        {activeTimelineEntry.parameterAlerts.map(
                          (alert, index) => (
                            <li key={index}>{alert}</li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      )}

      {/* Constants Modal */}
      <Modal show={showConstants} onHide={() => setShowConstants(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Constants</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(['C1', 'C2', 'C3', 'C4', 'PI'] as (keyof Constants)[]).map(k => (
            <Form.Group key={k} className="mb-2">
              <Form.Label>{k}</Form.Label>
              <Form.Control
                value={constants[k]}
                onChange={e => handleConstantChange(k, e.target.value)}
              />
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowConstants(false)}>Done</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PressureDropCalculator;
